import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Sun, Moon, Download, Upload, Printer, ChevronDown, ChevronUp, Battery } from 'lucide-react';

// --- Helper Functions ---
const formatCurrency = (value, decimals = 0) => {
    if (value == null || isNaN(value) || !isFinite(value)) return 'N/A';
    const options = { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals };
    return new Intl.NumberFormat('en-US', options).format(value);
};

const formatPercentage = (value) => {
    if (value == null || isNaN(value) || !isFinite(value)) return 'N/A';
    return `${(Number(value) * 100).toFixed(2)}%`;
};

const formatYears = (value) => {
    if (value == null || isNaN(value) || !isFinite(value)) return 'N/A';
    return `${value.toFixed(2)} years`;
}

// --- BESS Revenue Models ---
const calculateBessRevenueStreams = (inputs, solarGeneration) => {
    if (!inputs.bessEnabled) return { arbitrageRevenue: 0, ancillaryRevenue: 0, capacityRevenue: 0, demandChargeReduction: 0, totalBessRevenue: 0 };
    
    // Energy Arbitrage: Buy low, sell high
    const arbitrageCycles = inputs.bessArbitrageCycles; // cycles per day
    const arbitrageSpread = inputs.onGridTariff * inputs.bessArbitrageSpread; // price differential
    const arbitrageEfficiency = inputs.bessRoundTripEfficiency;
    const arbitrageRevenue = inputs.bessEnergyCapacity * arbitrageCycles * 365 * arbitrageSpread * arbitrageEfficiency;
    
    // Ancillary Services (frequency regulation, etc.)
    const ancillaryRevenue = inputs.bessPowerCapacity * inputs.bessAncillaryRate; // $/MW/year
    
    // Capacity/Demand Charge Reduction
    const capacityRevenue = inputs.bessPowerCapacity * inputs.bessCapacityRate * 12; // $/MW/month
    
    // Solar + Storage Value Stacking
    const demandChargeReduction = inputs.bessPowerCapacity * inputs.bessDemandChargeReduction * 12;
    
    const totalBessRevenue = arbitrageRevenue + ancillaryRevenue + capacityRevenue + demandChargeReduction;
    
    return {
        arbitrageRevenue,
        ancillaryRevenue,
        capacityRevenue,
        demandChargeReduction,
        totalBessRevenue
    };
};

// --- Enhanced Financial Calculation Engine with BESS ---
const calculateFinancials = (inputs) => {
    
    // Input validation and edge case protection
    const validateInputs = (inputs) => {
        const validated = { ...inputs };
        
        // Prevent division by zero
        validated.depreciationPeriod = Math.max(1, validated.depreciationPeriod);
        validated.operationPeriod = Math.max(1, validated.operationPeriod);
        validated.bessReplacementPeriod = Math.max(1, validated.bessReplacementPeriod);
        
        // Prevent invalid percentages
        validated.pvDegradationRate = Math.max(0, Math.min(0.5, validated.pvDegradationRate)); // Max 50% degradation
        validated.bessDegradationRate = Math.max(0, Math.min(0.5, validated.bessDegradationRate)); // Max 50% degradation
        validated.bessRoundTripEfficiency = Math.max(0.1, Math.min(1, validated.bessRoundTripEfficiency)); // 10%-100%
        validated.salvageValueRate = Math.max(0, Math.min(0.5, validated.salvageValueRate)); // 0%-50%
        
        // Prevent negative values where inappropriate
        validated.installedCapacity = Math.max(0.1, validated.installedCapacity);
        validated.bessEnergyCapacity = Math.max(0, validated.bessEnergyCapacity);
        validated.bessPowerCapacity = Math.max(0, validated.bessPowerCapacity);
        validated.onGridTariff = Math.max(0.001, validated.onGridTariff); // Minimum $0.001/kWh
        
        // Add additional bounds validation
        validated.annualHours = Math.max(0, Math.min(8760, validated.annualHours));
        validated.constructionPeriod = Math.max(1, Math.min(10, validated.constructionPeriod));
        validated.investmentPerMW = Math.max(1000, Math.min(10000000, validated.investmentPerMW));
        validated.longTermLoanRate = Math.max(0, Math.min(0.5, validated.longTermLoanRate));
        validated.discountRate = Math.max(0.01, Math.min(0.5, validated.discountRate));
        
        return validated;
    };
    
    const validInputs = validateInputs(inputs);

    const calculateInvestmentAndFinancing = () => {
        const solarInvestment = validInputs.installedCapacity * validInputs.investmentPerMW;
        
        // BESS Investment Calculation
        let bessInvestment = 0;
        if (validInputs.bessEnabled) {
            bessInvestment = (validInputs.bessEnergyCapacity * validInputs.bessCostPerMWh) + 
                           (validInputs.bessPowerCapacity * validInputs.bessPowerCostPerMW);
        }
        
        const totalInvestmentEstimate = solarInvestment + bessInvestment;
        const capitalFundRatio = totalInvestmentEstimate > 0 ? validInputs.ownFunds / totalInvestmentEstimate : 0;
        const loanRatio = 1 - capitalFundRatio;
        const constructionLoan = totalInvestmentEstimate * loanRatio;

        const equipmentPurchase = totalInvestmentEstimate * 0.7;
        const installationProject = totalInvestmentEstimate * 0.15;
        const otherConstructionCosts = totalInvestmentEstimate * 0.1;
        const preparatoryWork = totalInvestmentEstimate * 0.05;
        const totalStaticInvestment = equipmentPurchase + installationProject + otherConstructionCosts + preparatoryWork;
        const interestDuringConstruction = constructionLoan * validInputs.longTermLoanRate * validInputs.constructionPeriod;
        const workingCapital = totalInvestmentEstimate * 0.02;
        const totalInvestment = totalStaticInvestment + interestDuringConstruction + workingCapital;
        
        const capital = validInputs.ownFunds;
        const loan = totalInvestment - capital;
        const totalFinancing = capital + loan;

        return {
            totalInvestment,
            totalStaticInvestment,
            workingCapital,
            financing: { capital, loan, totalFinancing },
            loanDetails: { constructionLoan, capitalFundRatio },
            interestDuringConstruction,
            solarInvestment,
            bessInvestment
        };
    };

    const calculateLoanRepayment = (loanAmount, loanRate, term) => {
        const schedule = [];
        let remainingLoan = loanAmount;
        const annualPrincipalPayment = term > 0 ? loanAmount / term : 0;

        for (let year = 1; year <= term; year++) {
            if (remainingLoan <= 0.01) {
                schedule.push({ year, principal: 0, interest: 0, totalPayment: 0, remainingLoan: 0 });
                continue;
            }
            const interestPayment = remainingLoan * loanRate;
            const principalPayment = Math.min(remainingLoan, annualPrincipalPayment);
            const totalPayment = principalPayment + interestPayment;
            remainingLoan -= principalPayment;
            schedule.push({ year, principal: principalPayment, interest: interestPayment, totalPayment, remainingLoan });
        }
        return schedule;
    };
    
    const calculateIndicators = (projectCF, capitalCF, discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase) => {
        const calculateIRR = (cashflows, maxIterations = 100, tolerance = 1e-7) => {
            // Edge case: all positive or all negative cash flows
            const hasPositive = cashflows.some(cf => cf > 0);
            const hasNegative = cashflows.some(cf => cf < 0);
            if (!hasPositive || !hasNegative) return 0; // No IRR exists
            
            // Edge case: no initial investment
            if (cashflows[0] >= 0) return 0;
            
            let guess = 0.1;
            let oscillationCount = 0;
            let lastGuess = guess;
            
            for (let i = 0; i < maxIterations; i++) {
                const currentGuess = guess;
                const npv = cashflows.reduce((acc, val, j) => acc + val / Math.pow(1 + currentGuess, j), 0);
                const derivative = cashflows.reduce((acc, val, j) => acc - j * val / Math.pow(1 + currentGuess, j + 1), 0);
                
                // Edge case: derivative too small (near zero)
                if (Math.abs(derivative) < tolerance) break;
                
                const newGuess = guess - npv / derivative;
                
                // Edge case: invalid calculation results
                if (isNaN(newGuess) || !isFinite(newGuess)) break;
                
                // Edge case: extremely negative rates (project failure)
                if (newGuess < -0.99) return -0.99;
                
                // Check for oscillation
                if (Math.abs(newGuess - lastGuess) < tolerance * 10) {
                    oscillationCount++;
                    if (oscillationCount > 3) return (newGuess + lastGuess) / 2;
                } else {
                    oscillationCount = 0;
                }
                
                // Convergence check
                if (Math.abs(newGuess - guess) < tolerance) return newGuess;
                
                lastGuess = guess;
                guess = newGuess;
            }
            return isNaN(guess) || !isFinite(guess) ? 0 : Math.max(guess, -0.99);
        };

        const projectNPV = projectCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const capitalNPV = capitalCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const projectIRR = calculateIRR(projectCF);
        const capitalIRR = calculateIRR(capitalCF);

        let cumulativeCF = 0;
        let paybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            cumulativeCF += projectCF[i];
            if(cumulativeCF >= -projectCF[0]) {
                paybackPeriod = (i - 1) + (-projectCF[0] - (cumulativeCF - projectCF[i])) / projectCF[i];
                break;
            }
        }
        
        let cumulativeDiscountedCF = 0;
        let discountedPaybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            const discountedCF = projectCF[i] / Math.pow(1 + discountRate, i);
            cumulativeDiscountedCF += discountedCF;
            if(cumulativeDiscountedCF >= -projectCF[0]) {
                 const prevCumulativeDiscountedCF = cumulativeDiscountedCF - discountedCF;
                 discountedPaybackPeriod = (i - 1) + (-projectCF[0] - prevCumulativeDiscountedCF) / discountedCF;
                 break;
            }
        }

        // Enhanced LCOE Calculation including BESS (O&M + CapEx)
        const sumDiscountedOandM = costStatement.reduce((acc, cost) => {
            return acc + (cost.operatingCost + cost.landCost + (cost.bessOperatingCost || 0)) / Math.pow(1 + discountRate, cost.year);
        }, 0);
        
        // Include BESS replacement CapEx in LCOE calculation
        const sumDiscountedBessCapex = bessStatement.reduce((acc, bess) => {
            return acc + (bess.replacementCapex || 0) / Math.pow(1 + discountRate, bess.year);
        }, 0);
        
        const salvageValue = depreciableBase * validInputs.salvageValueRate;
        const discountedSalvageValue = salvageValue / Math.pow(1 + discountRate, validInputs.operationPeriod);

        const totalDiscountedCosts = totalInvestment + sumDiscountedOandM + sumDiscountedBessCapex - discountedSalvageValue;

        const discountedEnergy = pnlStatement.reduce((acc, pnl) => {
            return acc + pnl.annualGeneration / Math.pow(1 + discountRate, pnl.year);
        }, 0);

        const lcoe = discountedEnergy > 0 ? totalDiscountedCosts / discountedEnergy : 0;

        return { projectNPV, capitalNPV, projectIRR, capitalIRR, paybackPeriod, discountedPaybackPeriod, lcoe };
    };

    const outputs = {};
    const { totalInvestment, totalStaticInvestment, workingCapital, financing, loanDetails, interestDuringConstruction, solarInvestment, bessInvestment } = calculateInvestmentAndFinancing();
    outputs.investmentPlan = { totalInvestment, totalStaticInvestment, workingCapital, ...loanDetails, interestDuringConstruction, solarInvestment, bessInvestment };
    outputs.financing = financing;

    const depreciableBase = totalStaticInvestment + interestDuringConstruction;
    const annualDepreciation = validInputs.depreciationPeriod > 0 ? (depreciableBase * (1 - validInputs.salvageValueRate)) / validInputs.depreciationPeriod : 0;
    outputs.loanRepaymentSchedule = calculateLoanRepayment(financing.loan, validInputs.longTermLoanRate, validInputs.operationPeriod);

    // BESS Asset Tracking for proper depreciation of replacements
    const bessAssets = [];
    if (validInputs.bessEnabled) {
        // Initial BESS asset
        bessAssets.push({
            installYear: 0,
            cost: bessInvestment,
            depreciationPeriod: validInputs.bessReplacementPeriod,
            annualDepreciation: bessInvestment / validInputs.bessReplacementPeriod
        });
    }

    const pnlStatement = [], costStatement = [], cashFlowStatement = [], balanceSheet = [], evaStatement = [], bessStatement = [];
    let accumulatedRetainedEarnings = 0;
    let accumulatedStatutoryReserve = 0;
    let accumulatedDiscretionaryReserve = 0;

    for (let year = 1; year <= validInputs.operationPeriod; year++) {
        const pnl = {}, cost = {}, cf = {}, balance = {}, eva = {}, bess = {};
        const degradationFactor = Math.pow((1 - validInputs.pvDegradationRate), year - 1);
        const annualGeneration = (validInputs.installedCapacity * 1000 * validInputs.annualHours) * degradationFactor;
        const loanInterest = (outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year - 1]) ? outputs.loanRepaymentSchedule[year - 1].interest : 0;

        // BESS Degradation and Replacement
        let bessDegradationFactor = 1;
        let bessReplacementCapex = 0;
        
        if (validInputs.bessEnabled) {
            bessDegradationFactor = Math.pow((1 - validInputs.bessDegradationRate), year - 1);
            
            // BESS replacement schedule - treat as CapEx, not OpEx
            if (year > 0 && year % validInputs.bessReplacementPeriod === 0 && year < validInputs.operationPeriod) {
                const replacementCost = bessInvestment * validInputs.bessReplacementCostFactor;
                bessReplacementCapex = replacementCost;
                
                // Add new BESS asset for depreciation tracking
                bessAssets.push({
                    installYear: year,
                    cost: replacementCost,
                    depreciationPeriod: validInputs.bessReplacementPeriod,
                    annualDepreciation: replacementCost / validInputs.bessReplacementPeriod
                });
            }
        }

        pnl.year = year;
        pnl.annualGeneration = annualGeneration;
        pnl.operatingIncome = annualGeneration * validInputs.onGridTariff;
        
        // BESS Revenue Streams (with proper degradation by revenue type)
        const bessRevenues = calculateBessRevenueStreams(validInputs, annualGeneration);
        if (validInputs.bessEnabled) {
            // Energy-based revenues degrade, power-based revenues don't
            const degradedArbitrage = bessRevenues.arbitrageRevenue * bessDegradationFactor;
            const degradedAncillary = bessRevenues.ancillaryRevenue * bessDegradationFactor;
            const stableCapacity = bessRevenues.capacityRevenue; // No degradation
            const stableDemandReduction = bessRevenues.demandChargeReduction; // No degradation
            pnl.bessRevenue = degradedArbitrage + degradedAncillary + stableCapacity + stableDemandReduction;
        } else {
            pnl.bessRevenue = 0;
        }
        pnl.totalRevenue = pnl.operatingIncome + pnl.bessRevenue;
        
        cost.year = year;
        
        // Calculate land costs (annual lease/rent)
        const totalAcres = validInputs.installedCapacity * validInputs.acresPerMW;
        const annualLandCost = totalAcres * validInputs.landCostPerAcre;
        
        if (year === 1) {
            cost.operatingCost = pnl.operatingIncome * validInputs.operatingCostRate;
        } else {
            const previousYearCost = costStatement[costStatement.length - 1].operatingCost;
            cost.operatingCost = previousYearCost * (1 + validInputs.operatingCostEscalationRate);
        }
        
        cost.landCost = annualLandCost;

        // BESS Operating Costs (pure O&M only, no CapEx)
        cost.bessOperatingCost = 0;
        if (validInputs.bessEnabled) {
            cost.bessOperatingCost = validInputs.bessPowerCapacity * validInputs.bessOperatingCostRate;
        }

        // Solar Depreciation
        cost.depreciation = year <= validInputs.depreciationPeriod ? annualDepreciation : 0;
        
        // BESS Depreciation (multiple assets with staggered replacement)
        cost.bessDepreciation = 0;
        if (validInputs.bessEnabled) {
            cost.bessDepreciation = bessAssets.reduce((total, asset) => {
                const assetAge = year - asset.installYear;
                if (assetAge > 0 && assetAge <= asset.depreciationPeriod) {
                    return total + asset.annualDepreciation;
                }
                return total;
            }, 0);
        }
        cost.interest = loanInterest;
        cost.totalCost = cost.operatingCost + cost.depreciation + cost.bessDepreciation + cost.interest + cost.landCost + cost.bessOperatingCost;
        cost.costPerKwh = annualGeneration > 0 ? cost.totalCost / annualGeneration : 0;
        
        pnl.vatSurtax = pnl.totalRevenue * validInputs.vatRate * validInputs.surtaxRate;
        pnl.totalProfit = pnl.totalRevenue - cost.totalCost - pnl.vatSurtax;
        pnl.incomeTax = pnl.totalProfit > 0 ? pnl.totalProfit * validInputs.incomeTaxRate : 0;
        pnl.netProfit = pnl.totalProfit - pnl.incomeTax;
        
        const statutoryReserve = pnl.netProfit > 0 ? pnl.netProfit * validInputs.statutoryReserveRate : 0;
        const discretionaryReserve = pnl.netProfit > 0 ? pnl.netProfit * validInputs.discretionaryReserveRate : 0;
        const profitAvailableForDistribution = pnl.netProfit - statutoryReserve - discretionaryReserve;
        pnl.profitDistribution = profitAvailableForDistribution > 0 ? profitAvailableForDistribution * validInputs.profitDistributionRate : 0;
        pnl.retainedEarnings = profitAvailableForDistribution - pnl.profitDistribution;
        
        accumulatedStatutoryReserve += statutoryReserve;
        accumulatedDiscretionaryReserve += discretionaryReserve;
        accumulatedRetainedEarnings += pnl.retainedEarnings;

        // BESS Performance Tracking
        bess.year = year;
        bess.energyCapacity = validInputs.bessEnabled ? validInputs.bessEnergyCapacity * bessDegradationFactor : 0;
        bess.powerCapacity = validInputs.bessEnabled ? validInputs.bessPowerCapacity : 0;
        // Energy-based revenues degrade with battery capacity
        bess.arbitrageRevenue = bessRevenues.arbitrageRevenue * bessDegradationFactor;
        bess.ancillaryRevenue = bessRevenues.ancillaryRevenue * bessDegradationFactor;
        // Power-based revenues don't degrade significantly (MW capacity vs MWh capacity)
        bess.capacityRevenue = bessRevenues.capacityRevenue;
        bess.demandChargeReduction = bessRevenues.demandChargeReduction;
        bess.operatingCost = cost.bessOperatingCost;
        bess.replacementCapex = bessReplacementCapex; // Now tracked as CapEx
        bess.bessDepreciation = cost.bessDepreciation;
        bess.degradationFactor = bessDegradationFactor;

        cf.year = year;
        cf.netCashFlow_op = pnl.netProfit + cost.depreciation + cost.bessDepreciation;
        
        balance.year = year;
        
        // Calculate total fixed assets including all BESS assets
        const solarFixedAssets = Math.max(0, depreciableBase - (annualDepreciation * Math.min(year, validInputs.depreciationPeriod)));
        const bessFixedAssets = bessAssets.reduce((total, asset) => {
            const assetAge = year - asset.installYear;
            if (assetAge >= 0) {
                const accumulatedDepreciation = Math.min(assetAge, asset.depreciationPeriod) * asset.annualDepreciation;
                return total + Math.max(0, asset.cost - accumulatedDepreciation);
            }
            return total;
        }, 0);
        const fixedAssets = solarFixedAssets + bessFixedAssets;
        
        const cashFromOps = (balanceSheet[year-2]?.currentAssets || workingCapital) + cf.netCashFlow_op;
        const financingActivities = - ((outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year-1]) ? outputs.loanRepaymentSchedule[year-1].principal : 0) - pnl.profitDistribution - bessReplacementCapex;
        balance.currentAssets = cashFromOps + financingActivities;
        balance.totalAssets = fixedAssets + balance.currentAssets;
        
        balance.longTermDebt = (outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year - 1]) ? outputs.loanRepaymentSchedule[year - 1].remainingLoan : 0;
        balance.currentLiabilities = 0; 
        balance.totalLiabilities = balance.longTermDebt + balance.currentLiabilities;
        
        balance.equity = financing.capital;
        balance.accumulatedProfit = accumulatedRetainedEarnings;
        balance.totalEquity = balance.equity + accumulatedRetainedEarnings + accumulatedStatutoryReserve + accumulatedDiscretionaryReserve;
        balance.totalLiabilitiesAndEquity = balance.totalLiabilities + balance.totalEquity;
        balance.check = balance.totalAssets - balance.totalLiabilitiesAndEquity;

        const nopat = pnl.netProfit + loanInterest * (1 - validInputs.incomeTaxRate);
        eva.year = year;
        const capitalEmployed = (balanceSheet[year-2]?.totalAssets || totalInvestment) - (balanceSheet[year-2]?.currentLiabilities || 0);
        eva.eva = nopat - capitalEmployed * validInputs.discountRate;

        pnlStatement.push(pnl); costStatement.push(cost); cashFlowStatement.push(cf); balanceSheet.push(balance); evaStatement.push(eva); bessStatement.push(bess);
    }
    
    outputs.pnlStatement = pnlStatement; outputs.costStatement = costStatement; outputs.cashFlowStatement = cashFlowStatement; outputs.balanceSheet = balanceSheet; outputs.evaStatement = evaStatement; outputs.bessStatement = bessStatement;
    
    const projectCF = [-totalInvestment, ...pnlStatement.map(p => {
        const costForYear = costStatement.find(c=>c.year===p.year);
        return p.netProfit + (p.interest || 0) * (1 - validInputs.incomeTaxRate) + (costForYear?.depreciation || 0) + (costForYear?.bessDepreciation || 0);
    })];
    const capitalCF = [-financing.capital, ...pnlStatement.map(p => p.profitDistribution)];
    outputs.indicators = calculateIndicators(projectCF, capitalCF, validInputs.discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase);
    
    return outputs;
};


// --- UI Components ---
const Tab = ({ label, isActive, onClick }) => (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 focus:outline-none ${isActive ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
        {label}
    </button>
);

const InputCell = ({ id, value, onChange, label, unit, isPercentage = false, step = "any" }) => {
    // Helper function to round values for display
    const roundForDisplay = (val) => {
        if (val == null || isNaN(val) || !isFinite(val)) return '';
        
        // Determine decimal places based on step or value magnitude
        let decimals = 2; // default
        if (step !== "any") {
            const stepNum = parseFloat(step);
            if (stepNum >= 1) decimals = 0;
            else if (stepNum >= 0.1) decimals = 1;
            else if (stepNum >= 0.01) decimals = 2;
            else decimals = 4;
        } else {
            // Auto-determine based on value magnitude
            if (Math.abs(val) >= 1000) decimals = 0;
            else if (Math.abs(val) >= 10) decimals = 1;
            else if (Math.abs(val) >= 0.01) decimals = 2;
            else decimals = 4;
        }
        
        return parseFloat(val.toFixed(decimals));
    };

    const displayValue = isPercentage ? roundForDisplay(value * 100) : roundForDisplay(value);
    
    return (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md">
        <label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <div className="flex items-center">
            <input 
                type="number" 
                id={id} 
                    value={displayValue} 
                onChange={e => onChange(id, isPercentage ? e.target.value / 100 : e.target.value)} 
                className="w-full px-3 py-1.5 text-right bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                step={step}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400 w-24 text-right pr-2">{unit}</span>
            </div>
        </div>
    );
};

const CheckboxCell = ({ id, checked, onChange, label, description }) => (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md">
        <div>
            <label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
            {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
        </div>
        <div className="flex justify-end">
            <input 
                type="checkbox" 
                id={id} 
                checked={checked}
                onChange={e => onChange(id, e.target.checked)} 
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
        </div>
    </div>
);

const OutputCell = ({ label, value, unit, formatter = (v) => formatCurrency(v) }) => (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <div className="flex justify-end items-center space-x-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-md w-full text-right">{formatter(value)}</span>
            {unit && <span className="text-sm text-gray-500 dark:text-gray-400 w-24">{unit}</span>}
        </div>
    </div>
);

const SectionHeader = ({ title }) => (
    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-700/50 p-3 my-4 rounded-md">{title}</h3>
);

const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// --- Main App Component ---
export default function App() {
    const [activeTab, setActiveTab] = useState('Summary');
    const [isLoadingGoalSeek, setIsLoadingGoalSeek] = useState(false);
    const [goalSeekMessage, setGoalSeekMessage] = useState('');
    const [isDarkMode, setIsDarkMode] = useState(true);
    const fileInputRef = React.useRef(null);

    const [inputs, setInputs] = useState({
        // Project Fundamentals - Optimized for Philippines Visayas Region
        constructionPeriod: 1, operationPeriod: 25, installedCapacity: 50,
        investmentPerMW: 1250000, // $1.25M/MW - competitive for Philippines market
        annualHours: 1950, // ~5.35 kWh/m²/day excellent Visayas solar resource
        onGridTariff: 0.198, // $0.198/kWh (~P11.78/kWh current Visayan Electric rates)
        pvDegradationRate: 0.005, // 0.5% per year (tropical climate consideration)
        operatingCostRate: 0.08, // 8% of revenue (optimized for Philippines)
        operatingCostEscalationRate: 0.025, // 2.5% inflation (Philippines target rate)
        ownFunds: 12500000, // $12.5M equity for 50 MW project (20% equity ratio)
        longTermLoanRate: 0.075, // 7.5% (competitive Philippines renewable financing)
        depreciationPeriod: 20, // 20 years (optimized tax depreciation)
        salvageValueRate: 0.08, // 8% salvage value (higher for quality equipment)
        vatRate: 0.12, // 12% (Philippines VAT rate)
        surtaxRate: 0.10, // 10% (optimized surtax)
        incomeTaxRate: 0.25, // 25% (Philippines corporate tax rate)
        statutoryReserveRate: 0.10, discretionaryReserveRate: 0.05, profitDistributionRate: 0.75,
        discountRate: 0.08, // 8% (appropriate for Philippines renewable projects)
        goalSeekTargetIRR: 0.12, // 12% target IRR (competitive for market)
        landCostPerAcre: 2500, // $2,500/acre (Philippines agricultural land lease)
        acresPerMW: 4.5, // 4.5 acres/MW (optimized land use efficiency)
        // BESS Parameters - Enabled by default for competitive edge
        bessEnabled: true,
        bessEnergyCapacity: 100, // 100 MWh (2-hour storage for 50 MW)
        bessPowerCapacity: 50, // 50 MW (1:1 ratio with solar)
        bessCostPerMWh: 280000, // $280k/MWh (competitive current market pricing)
        bessPowerCostPerMW: 180000, // $180k/MW (optimized power electronics cost)
        bessRoundTripEfficiency: 0.87, // 87% (latest LFP technology efficiency)
        bessDegradationRate: 0.018, // 1.8%/year (optimized battery degradation)
        bessOperatingCostRate: 12000, // $12k/MW/year (reduced O&M costs)
        bessReplacementPeriod: 15, // 15 years replacement cycle
        bessReplacementCostFactor: 0.55, // 55% of original cost (technology learning curve)
        // BESS Revenue Streams - Optimized for Philippines Grid Services
        bessArbitrageCycles: 1.2, // 1.2 cycles/day (peak shaving + night dispatch)
        bessArbitrageSpread: 0.35, // 35% price spread (Philippines grid volatility)
        bessAncillaryRate: 28000, // $28k/MW/year (frequency regulation services)
        bessCapacityRate: 6000, // $6k/MW/month (capacity payments)
        bessDemandChargeReduction: 9500, // $9.5k/MW/month (peak demand reduction)
    });

    const [outputs, setOutputs] = useState({});

    useEffect(() => {
        try {
            const result = calculateFinancials(inputs);
            if (result && typeof result === 'object') {
                setOutputs(result);
            }
        } catch (error) {
            console.error('Calculation error:', error);
            setOutputs({}); // Safe fallback
        }
    }, [inputs]);

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    const handleInputChange = (id, value) => {
        const numValue = Number(value);
        if (isNaN(numValue) || !isFinite(numValue)) return;
        
        // Quick bounds check
        const bounds = {
            installedCapacity: [0.1, 10000],
            annualHours: [0, 8760],
            onGridTariff: [0.001, 1.0],
            operationPeriod: [1, 50],
            constructionPeriod: [1, 10],
            investmentPerMW: [1000, 10000000],
            longTermLoanRate: [0, 0.5],
            discountRate: [0.01, 0.5]
        };
        
        if (bounds[id]) {
            const [min, max] = bounds[id];
            if (numValue < min || numValue > max) return;
        }
        
        setInputs(prev => ({ ...prev, [id]: numValue }));
    };

    let exportInProgress = false;
    const handleExport = () => {
        if (exportInProgress) return;
        exportInProgress = true;
        
        const header = Object.keys(inputs).join(',');
        const values = Object.values(inputs).join(',');
        const csvContent = `${header}\n${values}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'solar_model_parameters.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        
        setTimeout(() => { exportInProgress = false; }, 1000);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImport = (event) => {
        const file = event.target.files[0];
        if (!file || !file.name.endsWith('.csv') || file.size > 1024 * 1024) {
            alert('Please select a valid CSV file under 1MB');
            return;
        }
        
            const reader = new FileReader();
        reader.onerror = () => alert('Error reading file');
            reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n').filter(line => line.trim());
                if (lines.length < 2) throw new Error('Invalid CSV format');
                
                    const header = lines[0].split(',');
                    const values = lines[1].split(',');
                    const newInputs = {};
                
                    header.forEach((key, index) => {
                    const value = Number(values[index]);
                    if (!isNaN(value) && isFinite(value)) {
                        newInputs[key.trim()] = value;
                    }
                    });
                
                    setInputs(prev => ({...prev, ...newInputs}));
            } catch (error) {
                alert('Error parsing CSV: ' + error.message);
                }
            };
            reader.readAsText(file);
    };

    const handlePrint = async () => {
        const printableTabs = tabs.filter(tab => tab !== 'README');
        
        let allTabsContent = '';
        
        // Generate content for each tab
        for (let i = 0; i < printableTabs.length; i++) {
            const tab = printableTabs[i];
            try {
                let tabHtml = '';
                
                // Render tab content without Explanation components
                switch (tab) {
                    case 'Summary':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <div class="summary-print-container">
                                    <h2 class="text-2xl font-bold text-gray-800 mb-4">Financial Indicators Summary</h2>
                                    <div class="grid md:grid-cols-2 gap-6">
                                        <div class="bg-white p-4 rounded-lg border border-gray-200">
                                            <h3 class="font-semibold text-lg mb-3 text-blue-700">Project Investment Analysis</h3>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan?.totalInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                <div class="flex justify-between"><span>Project IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                                                <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                <div class="flex justify-between"><span>Discounted Payback Period:</span><span>${formatYears(outputs.indicators?.discountedPaybackPeriod)}</span></div>
                                            </div>
                                        </div>
                                        <div class="bg-white p-4 rounded-lg border border-gray-200">
                                            <h3 class="font-semibold text-lg mb-3 text-green-700">Capital Analysis</h3>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                                                <div class="flex justify-between"><span>Capital IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                    ${inputs.bessEnabled ? `
                                    <div class="bg-white p-4 rounded-lg border border-gray-200">
                                        <h3 class="font-semibold text-lg mb-3 text-purple-700 flex items-center">
                                            🔋 BESS Analysis
                                        </h3>
                                        <div class="space-y-2">
                                            <div class="flex justify-between"><span>BESS Investment:</span><span>${formatCurrency(outputs.investmentPlan?.bessInvestment)}</span></div>
                                            <div class="flex justify-between"><span>BESS % of Total:</span><span>${formatPercentage((outputs.investmentPlan?.bessInvestment || 0) / (outputs.investmentPlan?.totalInvestment || 1))}</span></div>
                                            <div class="flex justify-between"><span>Annual BESS Revenue:</span><span>${formatCurrency(outputs.bessStatement?.[0] ? (outputs.bessStatement[0].arbitrageRevenue + outputs.bessStatement[0].ancillaryRevenue + outputs.bessStatement[0].capacityRevenue + outputs.bessStatement[0].demandChargeReduction) : 0)}</span></div>
                                            <div class="flex justify-between"><span>Total BESS Revenue:</span><span>${formatCurrency(outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0)}</span></div>
                                        </div>
                                    </div>
                                    ` : ''}
                                    <div class="bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
                                        <h3 class="font-semibold text-lg mb-3 text-purple-700">Key Metrics</h3>
                                        <div class="grid md:grid-cols-2 gap-x-6 gap-y-4">
                                            <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>Avg. Cost per kWh:</span><span>${formatCurrency(outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / outputs.costStatement?.length, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>Total Net Profit:</span><span>${formatCurrency(outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0))}</span></div>
                                                                                         <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0))}</span></div>
                                        </div>
                                    </div>
                                    
                                    <div class="mt-8 kpi-section">
                                        <h2 class="text-xl font-bold mb-4">Key Performance Indicators (KPIs)</h2>
                                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-blue-700">Investment Viability</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Project IRR vs Discount:</span><span>${formatPercentage(outputs.indicators?.projectIRR)} vs ${formatPercentage(inputs.discountRate)}</span></div>
                                                    <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                    <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                                    <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-green-700">Investor Returns</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                                    <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                                                    <div class="flex justify-between"><span>Profit Distribution:</span><span>${formatPercentage(inputs.profitDistributionRate)}</span></div>
                                                    <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 class="text-lg font-semibold mb-3 text-purple-700">Financial Health</h3>
                                                <div class="space-y-2">
                                                    <div class="flex justify-between"><span>Operating Margin:</span><span>${formatPercentage(1 - inputs.operatingCostRate)}</span></div>
                                                    <div class="flex justify-between"><span>Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                                                    <div class="flex justify-between"><span>Viability Status:</span><span>${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'VIABLE' : 'NOT VIABLE'}</span></div>
                                                    <div class="flex justify-between"><span>NPV Status:</span><span>${(outputs.indicators?.projectNPV || 0) > 0 ? 'POSITIVE' : 'NEGATIVE'}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    case 'Basic Data':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <div class="grid lg:grid-cols-2 gap-8">
                                    <div class="bg-white p-6 rounded-lg border border-gray-200">
                                        <h2 class="text-xl font-semibold mb-4 text-blue-700">Project Configuration</h2>
                                        <div class="space-y-3">
                                            <div class="flex justify-between"><span>Construction Period:</span><span>${inputs.constructionPeriod} years</span></div>
                                            <div class="flex justify-between"><span>Operation Period:</span><span>${inputs.operationPeriod} years</span></div>
                                            <div class="flex justify-between"><span>Installed Capacity:</span><span>${inputs.installedCapacity} MW</span></div>
                                            <div class="flex justify-between"><span>Investment per MW:</span><span>${formatCurrency(inputs.investmentPerMW)}</span></div>
                                            <div class="flex justify-between"><span>Own Funds:</span><span>${formatCurrency(inputs.ownFunds)}</span></div>
                                            <div class="flex justify-between"><span>Acres per MW:</span><span>${inputs.acresPerMW}</span></div>
                                        </div>
                                    </div>
                                    <div class="bg-white p-6 rounded-lg border border-gray-200">
                                        <h2 class="text-xl font-semibold mb-4 text-green-700">Revenue & Cost Parameters</h2>
                                        <div class="space-y-3">
                                            <div class="flex justify-between"><span>Annual Hours:</span><span>${inputs.annualHours} hours</span></div>
                                            <div class="flex justify-between"><span>On-Grid Tariff:</span><span>${formatCurrency(inputs.onGridTariff, 4)}/kWh</span></div>
                                            <div class="flex justify-between"><span>PV Degradation Rate:</span><span>${formatPercentage(inputs.pvDegradationRate)}</span></div>
                                            <div class="flex justify-between"><span>Operating Cost Rate:</span><span>${formatPercentage(inputs.operatingCostRate)}</span></div>
                                            <div class="flex justify-between"><span>Operating Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                                            <div class="flex justify-between"><span>Land Cost per Acre:</span><span>${formatCurrency(inputs.landCostPerAcre)}</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    case 'Investment & Financing':
                        if (outputs.investmentPlan) {
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <div class="space-y-6">
                                        <div>
                                            <h2 class="text-xl font-semibold mb-4">Investment Plan</h2>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Total Static Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalStaticInvestment)}</span></div>
                                                <div class="flex justify-between"><span>Working Capital:</span><span>${formatCurrency(outputs.investmentPlan.workingCapital)}</span></div>
                                            </div>
                                        </div>
                                        <div>
                                            <h2 class="text-xl font-semibold mb-4">Financing Structure</h2>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Capital (Equity):</span><span>${formatCurrency(outputs.investmentPlan.financing.capital)}</span></div>
                                                <div class="flex justify-between"><span>Loan:</span><span>${formatCurrency(outputs.investmentPlan.financing.loan)}</span></div>
                                                <div class="flex justify-between"><span>Total Financing:</span><span>${formatCurrency(outputs.investmentPlan.financing.totalFinancing)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `;
                        }
                        break;

                    case 'Loan Repayment':
                        if (outputs.loanRepayment && outputs.loanRepayment.length > 0) {
                            const tableRows = outputs.loanRepayment.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.openingBalance, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.interest, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.principal, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.closingBalance, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Opening Balance</th>
                                                <th class="border px-2 py-1">Interest</th>
                                                <th class="border px-2 py-1">Principal</th>
                                                <th class="border px-2 py-1">Closing Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Total Cost & Cost/kWh':
                        if (outputs.costStatement && outputs.costStatement.length > 0) {
                            const tableRows = outputs.costStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.landCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.depreciation, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.costPerKwh, 4)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Operating Cost</th>
                                                <th class="border px-2 py-1">Land Cost</th>
                                                <th class="border px-2 py-1">Depreciation</th>
                                                <th class="border px-2 py-1">Cost per kWh</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Profit & Loss':
                        if (outputs.pnlStatement && outputs.pnlStatement.length > 0) {
                            const tableRows = outputs.pnlStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingIncome, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.netProfit, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.totalProfit, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Operating Income</th>
                                                <th class="border px-2 py-1">Operating Cost</th>
                                                <th class="border px-2 py-1">Net Profit</th>
                                                <th class="border px-2 py-1">Total Profit</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Balance Sheet':
                        if (outputs.balanceSheet && outputs.balanceSheet.length > 0) {
                            const tableRows = outputs.balanceSheet.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.equity, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.longTermLoan, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.netFixedAssets, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.check, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">Equity</th>
                                                <th class="border px-2 py-1">Long Term Loan</th>
                                                <th class="border px-2 py-1">Net Fixed Assets</th>
                                                <th class="border px-2 py-1">Balance Check</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'EVA':
                        if (outputs.evaStatement && outputs.evaStatement.length > 0) {
                            const tableRows = outputs.evaStatement.slice(0, 15).map(row => 
                                `<tr>
                                    <td class="border px-2 py-1">${row.year}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.nopat, 0)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.investedCapital, 0)}</td>
                                    <td class="border px-2 py-1">${formatPercentage(row.wacc)}</td>
                                    <td class="border px-2 py-1">${formatCurrency(row.eva, 0)}</td>
                                </tr>`
                            ).join('');
                            
                            tabHtml = `
                                <div class="print-page">
                                    <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                    <table class="w-full text-sm border-collapse border">
                                        <thead>
                                            <tr class="bg-gray-100">
                                                <th class="border px-2 py-1">Year</th>
                                                <th class="border px-2 py-1">NOPAT</th>
                                                <th class="border px-2 py-1">Invested Capital</th>
                                                <th class="border px-2 py-1">WACC</th>
                                                <th class="border px-2 py-1">EVA</th>
                                            </tr>
                                        </thead>
                                        <tbody>${tableRows}</tbody>
                                    </table>
                                </div>
                            `;
                        }
                        break;

                    case 'Charts':
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <p class="text-gray-600 mb-4">Financial charts visualization summary:</p>
                                <div class="space-y-4">
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Profitability Trends</h3>
                                        <p class="text-sm text-gray-600">Operating Income and Net Profit trends over the project lifecycle.</p>
                                    </div>
                                    <div>
                                        <h3 class="text-lg font-semibold mb-2">Cost Structure Analysis</h3>
                                        <p class="text-sm text-gray-600">Breakdown of operating costs, land costs, depreciation, and interest over time.</p>
                                    </div>
                                    <div class="mt-6">
                                        <h3 class="text-lg font-semibold mb-2">Key Financial Indicators Summary</h3>
                                        <div class="grid md:grid-cols-2 gap-4">
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                                <div class="flex justify-between"><span>Project IRR:</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                                                <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                            </div>
                                            <div class="space-y-2">
                                                <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                                <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                                                <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        break;

                    default:
                        tabHtml = `
                            <div class="print-page">
                                <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                                <p class="text-gray-600">Content for ${tab} tab - refer to the digital version for full interactive content.</p>
                            </div>
                        `;
                        break;
                }
                
                allTabsContent += tabHtml;
            } catch (error) {
                console.error(`Error rendering ${tab}:`, error);
                allTabsContent += `
                    <div class="print-page">
                        <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
                        <p class="text-gray-600">Error loading content for ${tab}</p>
                    </div>
                `;
            }
        }

        // Create print frame
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        
        document.body.appendChild(printFrame);
        
        const frameDoc = printFrame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(`
            <html>
                <head>
                    <title>Solar Financial Model - Complete Report</title>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; }
                        .print-page { page-break-after: always; padding: 20px; }
                        .print-page:last-child { page-break-after: auto; }
                        h1 { font-size: 24px; margin-bottom: 20px; color: #1f2937; }
                        h2 { font-size: 18px; margin-bottom: 15px; color: #374151; }
                        h3 { font-size: 16px; margin-bottom: 10px; color: #4b5563; }
                        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
                        th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
                        th { background-color: #f3f4f6; font-weight: bold; }
                        .space-y-2 > * + * { margin-top: 8px; }
                        .space-y-3 > * + * { margin-top: 12px; }
                        .space-y-6 > * + * { margin-top: 24px; }
                        .grid { display: grid; gap: 20px; }
                        .md\\:grid-cols-2 { grid-template-columns: 1fr 1fr; }
                        .bg-white { background-color: white; }
                        .border { border: 1px solid #d1d5db; }
                        .rounded-lg { border-radius: 8px; }
                        .p-4 { padding: 16px; }
                        .p-6 { padding: 24px; }
                        .flex { display: flex; }
                        .justify-between { justify-content: space-between; }
                        .font-semibold { font-weight: 600; }
                        .text-blue-700 { color: #1d4ed8; }
                        .text-green-700 { color: #047857; }
                        .text-purple-700 { color: #7c3aed; }
                        .text-gray-600 { color: #4b5563; }
                        .text-gray-800 { color: #1f2937; }
                        @page { margin: 1in; size: letter; }
                        @media print {
                            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                            .print-page { page-break-after: always; }
                            .print-page:last-child { page-break-after: auto; }
                        }
                        
                        /* Summary Tab Print Optimization */
                        .summary-print-container {
                            font-size: 13px !important;
                            line-height: 1.4 !important;
                            margin: 0 !important;
                            padding: 12px !important;
                        }
                        .summary-print-container h2 {
                            font-size: 20px !important;
                            margin-bottom: 12px !important;
                            font-weight: bold !important;
                        }
                        .summary-print-container h3 {
                            font-size: 16px !important;
                            margin-bottom: 8px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container h4 {
                            font-size: 13px !important;
                            margin-bottom: 6px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container .grid {
                            gap: 12px !important;
                        }
                        .summary-print-container .p-4 {
                            padding: 10px !important;
                        }
                        .summary-print-container .p-6 {
                            padding: 12px !important;
                        }
                        .summary-print-container .mb-3 {
                            margin-bottom: 6px !important;
                        }
                        .summary-print-container .mb-4 {
                            margin-bottom: 8px !important;
                        }
                        .summary-print-container .mb-6 {
                            margin-bottom: 10px !important;
                        }
                        .summary-print-container .mt-8 {
                            margin-top: 16px !important;
                        }
                        .summary-print-container .space-y-4 > * + * {
                            margin-top: 8px !important;
                        }
                        .summary-print-container .text-lg {
                            font-size: 15px !important;
                        }
                        .summary-print-container .text-xl {
                            font-size: 16px !important;
                        }
                        .summary-print-container .text-2xl {
                            font-size: 20px !important;
                        }
                        .summary-print-container .text-sm {
                            font-size: 11px !important;
                        }
                        .summary-print-container .border {
                            border-width: 1px !important;
                        }
                        .summary-print-container .rounded-lg {
                            border-radius: 4px !important;
                        }
                        .summary-print-container .w-5 {
                            width: 16px !important;
                            height: 16px !important;
                        }
                        .summary-print-container .kpi-section {
                            page-break-before: always !important;
                        }
                        
                        /* Preserve Grid Layouts for Print */
                        .summary-print-container .grid.md\\:grid-cols-2 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .grid.grid-cols-1.md\\:grid-cols-3 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr 1fr !important;
                        }
                        .summary-print-container .grid.md\\:grid-cols-2.gap-x-6 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .md\\:col-span-2 {
                            grid-column: span 2 !important;
                        }
                    </style>
                </head>
                <body>
                    ${allTabsContent}
                </body>
            </html>
        `);
        frameDoc.close();

        setTimeout(() => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(printFrame);
            }, 100);
        }, 500);
    };


    const runGoalSeek = useCallback(() => {
        if (isLoadingGoalSeek) return;
        setIsLoadingGoalSeek(true);
        setGoalSeekMessage('');
        setTimeout(() => {
            const targetIRR = inputs.goalSeekTargetIRR;
            const tolerance = 1e-5;
            const maxIterations = 100;
            const getIrrForFunds = (funds) => {
                if (funds <= 0) return -1;
                try {
                    const result = calculateFinancials({ ...inputs, ownFunds: funds });
                    const irr = result?.indicators?.projectIRR;
                    return (typeof irr === 'number' && isFinite(irr)) ? irr : -1;
                } catch (error) {
                    console.error('Error calculating IRR for funds:', funds, error);
                    return -1;
                }
            };
            let lowFunds = 1;
            let highFunds = (inputs.installedCapacity * inputs.investmentPerMW) * 1.1;
            let irrAtLow = getIrrForFunds(lowFunds);
            let irrAtHigh = getIrrForFunds(highFunds);
            if (targetIRR < irrAtLow || targetIRR > irrAtHigh) {
                setGoalSeekMessage(`Target IRR (${formatPercentage(targetIRR)}) is unachievable. Must be between ${formatPercentage(irrAtLow)} and ${formatPercentage(irrAtHigh)}.`);
                setIsLoadingGoalSeek(false); return;
            }
            let midFunds = lowFunds;
            for (let i = 0; i < maxIterations; i++) {
                midFunds = lowFunds + (highFunds - lowFunds) / 2;
                if (Math.abs(midFunds - highFunds) < 0.01 || Math.abs(midFunds - lowFunds) < 0.01) break;
                const irrAtMid = getIrrForFunds(midFunds);
                if (Math.abs(irrAtMid - targetIRR) < tolerance) break;
                if (irrAtMid < targetIRR) { lowFunds = midFunds; } else { highFunds = midFunds; }
            }
            setInputs(prev => ({ ...prev, ownFunds: midFunds }));
            setGoalSeekMessage(`Solution found for Own Funds: ${formatCurrency(midFunds)}`);
            setIsLoadingGoalSeek(false);
        }, 100);
    }, [inputs, isLoadingGoalSeek]);

    const tabs = ['Summary', 'Basic Data', 'Investment & Financing', 'BESS Analysis', 'Loan Repayment', 'Total Cost & Cost/kWh', 'Profit & Loss', 'Balance Sheet', 'EVA', 'Charts', 'README'];

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'Basic Data': return <BasicDataTab inputs={inputs} outputs={outputs} onChange={handleInputChange} onGoalSeek={runGoalSeek} isLoading={isLoadingGoalSeek} goalSeekMessage={goalSeekMessage} />;
            case 'Summary': return <SummaryTab outputs={outputs} inputs={inputs} />;
            case 'Investment & Financing': return <InvestmentFinancingTab outputs={outputs} />;
            case 'BESS Analysis': return <BessTab outputs={outputs} inputs={inputs} />;
            case 'Loan Repayment': return <LoanRepaymentTab outputs={outputs} />;
            case 'Total Cost & Cost/kWh': return <TotalCostTab outputs={outputs} />;
            case 'Profit & Loss': return <ProfitLossTab outputs={outputs} />;
            case 'Balance Sheet': return <BalanceSheetTab outputs={outputs} />;
            case 'EVA': return <EVATab outputs={outputs} />;
            case 'Charts': return <ChartsTab outputs={outputs} inputs={inputs} isDarkMode={isDarkMode} />;
            case 'README': return <ReadmeTab outputs={outputs} inputs={inputs} isDarkMode={isDarkMode} />;
            default: return <SummaryTab outputs={outputs} inputs={inputs} />;
        }
    };

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8">
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        .printable-area, .printable-area * {
                            visibility: visible;
                        }
                        .printable-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                        }
                        .no-print {
                            display: none;
                        }
                        
                        /* Summary Tab Print Optimization */
                        .summary-print-container {
                            font-size: 13px !important;
                            line-height: 1.4 !important;
                            margin: 0 !important;
                            padding: 12px !important;
                        }
                        .summary-print-container h2 {
                            font-size: 20px !important;
                            margin-bottom: 12px !important;
                            font-weight: bold !important;
                        }
                        .summary-print-container h3 {
                            font-size: 16px !important;
                            margin-bottom: 8px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container h4 {
                            font-size: 13px !important;
                            margin-bottom: 6px !important;
                            font-weight: 600 !important;
                        }
                        .summary-print-container .grid {
                            gap: 12px !important;
                        }
                        .summary-print-container .p-4 {
                            padding: 10px !important;
                        }
                        .summary-print-container .p-6 {
                            padding: 12px !important;
                        }
                        .summary-print-container .mb-3 {
                            margin-bottom: 6px !important;
                        }
                        .summary-print-container .mb-4 {
                            margin-bottom: 8px !important;
                        }
                        .summary-print-container .mb-6 {
                            margin-bottom: 10px !important;
                        }
                        .summary-print-container .mt-8 {
                            margin-top: 16px !important;
                        }
                        .summary-print-container .space-y-4 > * + * {
                            margin-top: 8px !important;
                        }
                        .summary-print-container .text-lg {
                            font-size: 15px !important;
                        }
                        .summary-print-container .text-xl {
                            font-size: 16px !important;
                        }
                        .summary-print-container .text-2xl {
                            font-size: 20px !important;
                        }
                        .summary-print-container .text-sm {
                            font-size: 11px !important;
                        }
                        .summary-print-container .border {
                            border-width: 1px !important;
                        }
                        .summary-print-container .rounded-lg {
                            border-radius: 4px !important;
                        }
                        .summary-print-container .w-5 {
                            width: 16px !important;
                            height: 16px !important;
                        }
                        .summary-print-container .kpi-section {
                            page-break-before: always !important;
                        }
                        
                        /* Preserve Grid Layouts for Print */
                        .summary-print-container .grid.md\\:grid-cols-2 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .grid.grid-cols-1.md\\:grid-cols-3 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr 1fr !important;
                        }
                        .summary-print-container .grid.md\\:grid-cols-2.gap-x-6 {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                        }
                        .summary-print-container .md\\:col-span-2 {
                            grid-column: span 2 !important;
                        }
                    }
                `}
            </style>
            <header className="mb-6 flex justify-between items-center no-print">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Solar + BESS Financial Model</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Comprehensive analysis for solar and battery energy storage projects.</p>
                </div>
                <div className="flex items-center gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleImport} style={{ display: 'none' }} accept=".csv" />
                    <button onClick={handleImportClick} title="Import Parameters" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Upload size={20} />
                    </button>
                    <button onClick={handleExport} title="Export Parameters" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Download size={20} />
                    </button>
                     <button onClick={handlePrint} title="Print to PDF" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        <Printer size={20} />
                    </button>
                    <button onClick={() => setIsDarkMode(!isDarkMode)} title="Toggle Dark Mode" className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </header>
            <div className="no-print flex flex-wrap border-b border-gray-200 dark:border-gray-700">{tabs.map(tab => (<Tab key={tab} label={tab} isActive={activeTab === tab} onClick={() => setActiveTab(tab)} />))}</div>
            <main className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-b-lg shadow-lg printable-area">
                {renderActiveTab()}
            </main>
            <footer className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400 no-print"><p>Ikutu - Sustainable Energy Solutions</p></footer>
        </div>
    );
}

// --- Tab Components ---

const SummaryTab = ({ outputs, inputs }) => (
    <div className="summary-print-container">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Financial Indicators Summary</h2>
        <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-lg mb-3 text-blue-700 dark:text-blue-400">Project Investment Analysis</h3>
                <OutputCell label="Total Investment" value={outputs.investmentPlan?.totalInvestment} />
                <OutputCell label="Project NPV" value={outputs.indicators?.projectNPV} />
                <OutputCell label="Project IRR (after tax)" value={outputs.indicators?.projectIRR} formatter={formatPercentage} />
                <OutputCell label="Payback Period" value={outputs.indicators?.paybackPeriod} formatter={formatYears} />
                <OutputCell label="Discounted Payback Period" value={outputs.indicators?.discountedPaybackPeriod} formatter={formatYears} />
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-lg mb-3 text-green-700 dark:text-green-400">Capital Analysis</h3>
                <OutputCell label="Capital NPV" value={outputs.indicators?.capitalNPV} />
                <OutputCell label="Capital IRR (after tax)" value={outputs.indicators?.capitalIRR} formatter={formatPercentage} />
            </div>
                    {inputs.bessEnabled && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-lg mb-3 text-purple-700 dark:text-purple-400 flex items-center">
                                <Battery className="w-5 h-5 mr-2" />
                                BESS Analysis
                            </h3>
                            <OutputCell label="BESS Investment" value={outputs.investmentPlan?.bessInvestment} />
                            <OutputCell label="BESS % of Total" value={(outputs.investmentPlan?.bessInvestment || 0) / (outputs.investmentPlan?.totalInvestment || 1)} formatter={formatPercentage} />
                            <OutputCell label="Annual BESS Revenue" value={outputs.bessStatement?.[0] ? (outputs.bessStatement[0].arbitrageRevenue + outputs.bessStatement[0].ancillaryRevenue + outputs.bessStatement[0].capacityRevenue + outputs.bessStatement[0].demandChargeReduction) : 0} />
                            <OutputCell label="Total BESS Revenue" value={outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0} />
                        </div>
                    )}
             <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 md:col-span-2">
                <h3 className="font-semibold text-lg mb-3 text-purple-700 dark:text-purple-400">Key Metrics</h3>
                 <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                    <OutputCell label="LCOE" value={outputs.indicators?.lcoe} unit="USD/kWh" formatter={(v) => formatCurrency(v, 4)} />
                    <OutputCell label="Avg. Cost per kWh" value={outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / outputs.costStatement?.length} unit="USD/kWh" formatter={(v) => formatCurrency(v, 4)} />
                    <OutputCell label="Total Net Profit" value={outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0)} />
                    <OutputCell label="Total EVA" value={outputs.evaStatement?.reduce((a,b) => a + b.eva, 0)} />
                 </div>
            </div>
        </div>
        
        <div className="mt-8 kpi-section">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">🎯 Key Performance Indicators (KPIs)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Investment Viability Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Project IRR &gt; Discount Rate</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Indicates project viability</p>
                            <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                {formatPercentage(outputs.indicators?.projectIRR)} vs {formatPercentage(inputs.discountRate)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? '✅ VIABLE' : '❌ NOT VIABLE'}
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Project NPV</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Confirms value creation</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(outputs.indicators?.projectNPV)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.projectNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.projectNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">LCOE &lt; Market Price</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Ensures competitive positioning</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Payback Period &lt; 10 years</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Acceptable risk profile</p>
                            <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                {formatYears(outputs.indicators?.paybackPeriod)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.paybackPeriod || 0) < 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.paybackPeriod || 0) < 10 ? '✅ ACCEPTABLE' : '❌ TOO LONG'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Investor Return Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Capital IRR &gt; Target Return</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate equity returns</p>
                            <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                {formatPercentage(outputs.indicators?.capitalIRR)}
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Capital NPV</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Equity value creation</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(outputs.indicators?.capitalNPV)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.indicators?.capitalNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.indicators?.capitalNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">Stable Cash Distributions</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Predictable investor returns</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatPercentage(inputs.profitDistributionRate)} of profit
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Positive EVA</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Value creation above cost of capital</p>
                            <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                {formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}
                            </div>
                            <div className={`text-sm mt-1 ${(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? '✅ CREATING VALUE' : '❌ DESTROYING VALUE'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🏥 Financial Health Metrics</h3>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Debt Service Coverage &gt; 1.2</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate cash flow for debt service</p>
                            <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                                Calculated from annual cash flows
                            </div>
                        </div>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-green-800 dark:text-green-200">Operating Margin &gt; 20%</h4>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">Healthy operational efficiency</p>
                            <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                {formatPercentage(1 - inputs.operatingCostRate)}
                            </div>
                            <div className={`text-sm mt-1 ${(1 - inputs.operatingCostRate) > 0.2 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {(1 - inputs.operatingCostRate) > 0.2 ? '✅ HEALTHY' : '❌ LOW MARGIN'}
                            </div>
                        </div>
                        
                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">Cost Escalation &lt; 3%</h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Controlled cost growth</p>
                            <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                {formatPercentage(inputs.operatingCostEscalationRate)}
                            </div>
                            <div className={`text-sm mt-1 ${inputs.operatingCostEscalationRate < 0.03 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {inputs.operatingCostEscalationRate < 0.03 ? '✅ CONTROLLED' : '❌ HIGH ESCALATION'}
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Balance Sheet Balance = 0</h4>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Accounting accuracy verification</p>
                            <div className="mt-2 text-sm text-purple-600 dark:text-purple-400">
                                Verified in Balance Sheet tab
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Explanation title="How This Works">
            <p>The Summary tab provides a high-level overview of the project's financial viability. It aggregates the most critical metrics from all other sheets into one place.</p>
            <p><strong>Project Investment Analysis:</strong> These metrics evaluate the project on its own merits, independent of how it's financed. The Internal Rate of Return (IRR) is the discount rate at which the Net Present Value (NPV) of all cash flows equals zero. A project is typically considered viable if its IRR is greater than the discount rate (WACC).</p>
            <p><strong>Capital Analysis:</strong> These metrics evaluate the return specifically for the equity investors. It considers the timing and amount of their cash contributions (own funds) and the dividends they receive.</p>
                    <div>
              <h5 className="font-bold text-gray-700 dark:text-gray-300">Key Metrics Explained:</h5>
              <p><strong>LCOE (Levelized Cost of Energy):</strong> This is the average revenue per unit of electricity that would be required to recover all costs over the project's lifetime. It includes the initial investment and discounts all future costs and energy production to account for the time value of money. It's the best "apples-to-apples" metric for comparing different power generation technologies.</p>
              <p><strong>Average Cost per kWh:</strong> This is a simple accounting average of the total costs from the "Total Cost" tab divided by the total energy produced. Unlike LCOE, it does not include the initial investment and does not account for the time value of money, which is why it is typically lower.</p>
              <p><strong>The Bottom Line:</strong> LCOE represents the true lifetime break-even price for energy, making it a superior metric for investment decisions. The Average Cost per kWh is more of a simple operational efficiency metric for a given period.</p>
                    </div>
                    <div>
              <h5 className="font-bold text-gray-700 dark:text-gray-300">Land Cost Integration:</h5>
              <p><strong>Land Requirements:</strong> The model calculates land requirements based on industry standards of approximately 5 acres per MW of installed capacity. This reflects the space needed for solar panels, inverters, access roads, maintenance areas, and buffer zones around the installation.</p>
              <p><strong>Land Cost Structure:</strong> Land costs are treated as annual operating expenses (lease/rental payments) rather than upfront capital investments. This reflects the typical practice in large-scale solar development where land is leased rather than purchased. The default rate is $1,000 per acre per year, but this can be adjusted based on local market conditions.</p>
              <p><strong>Financial Impact:</strong> Land costs significantly impact project economics as they represent a substantial ongoing operating expense. For a 10 MW project requiring 50 acres, annual land costs of $50,000 accumulate to $1.25 million over the 25-year project life. This is integrated into:</p>
              <ul className="ml-4 list-disc text-gray-600 dark:text-gray-400">
                <li><strong>LCOE Calculation:</strong> Land costs are included as part of the annual operating expenses, increasing the true cost of energy</li>
                <li><strong>Cash Flow Analysis:</strong> Annual land payments reduce project cash flows, affecting NPV and IRR calculations</li>
                <li><strong>Sensitivity Analysis:</strong> The model includes land cost sensitivity tables showing how NPV varies with different land cost scenarios</li>
                <li><strong>Total Cost Structure:</strong> Land costs appear as a separate line item in the Total Cost table, providing transparency in cost allocation</li>
              </ul>
              <p><strong>Real-World Relevance:</strong> Including land costs makes the financial model more realistic and comprehensive. Land acquisition or leasing is often one of the largest project costs after equipment, and can significantly vary by geographic location, making it a critical factor in project site selection and feasibility assessment.</p>
            </div>
        </Explanation>
    </div>
);

const NpvSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, investmentRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseInvestment = inputs.investmentPerMW;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const investmentRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseInvestment * (1 + mod));

        const sensitivityData = investmentRange.map(inv => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, investmentPerMW: inv };
                return calculateFinancials(tempInputs).indicators.projectNPV;
            });
        });

        return { sensitivityData, tariffRange, investmentRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">NPV Sensitivity Analysis (Tariff vs. Investment Cost)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Inv/MW ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {investmentRange.map((inv, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(inv)}</th>
                                {sensitivityData[rowIndex].map((npv, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    const isNegative = npv < 0;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''} ${isNegative ? 'text-red-500' : ''}`}>
                                            {formatCurrency(npv)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const CapitalIrrSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, ownFundsRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseOwnFunds = inputs.ownFunds;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const ownFundsRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseOwnFunds * (1 + mod));

        const sensitivityData = ownFundsRange.map(funds => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, ownFunds: funds };
                return calculateFinancials(tempInputs).indicators.capitalIRR;
            });
        });

        return { sensitivityData, tariffRange, ownFundsRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Capital IRR Sensitivity Analysis (Tariff vs. Own Funds)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Own Funds ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ownFundsRange.map((funds, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(funds)}</th>
                                {sensitivityData[rowIndex].map((irr, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''}`}>
                                            {formatPercentage(irr)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ProjectIrrSensitivityTable = ({ inputs }) => {
    const { sensitivityData, waccRange, ownFundsRange } = useMemo(() => {
        const baseWacc = inputs.discountRate;
        const baseOwnFunds = inputs.ownFunds;

        const waccRange = [-0.02, -0.01, 0, 0.01, 0.02].map(mod => baseWacc + mod);
        const ownFundsRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseOwnFunds * (1 + mod));

        const sensitivityData = ownFundsRange.map(funds => {
            return waccRange.map(wacc => {
                const tempInputs = { ...inputs, ownFunds: funds, discountRate: wacc };
                return calculateFinancials(tempInputs).indicators.projectIRR;
            });
        });

        return { sensitivityData, waccRange, ownFundsRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Project IRR Sensitivity Analysis (WACC vs. Own Funds)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Own Funds ↓</span>
                                    <span className="font-normal text-xs">WACC →</span>
                                </div>
                            </th>
                            {waccRange.map((wacc, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatPercentage(wacc)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ownFundsRange.map((funds, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(funds)}</th>
                                {sensitivityData[rowIndex].map((irr, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''}`}>
                                            {formatPercentage(irr)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    Note: Project IRR is independent of the WACC (Discount Rate). The IRR values only change vertically with "Own Funds" because the financing structure affects the project's cash flows (e.g., interest payments).
                </p>
            </div>
        </div>
    );
};

const LandCostSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, landCostRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseLandCost = inputs.landCostPerAcre;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const landCostRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => baseLandCost * (1 + mod));

        const sensitivityData = landCostRange.map(landCost => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, landCostPerAcre: landCost };
                return calculateFinancials(tempInputs).indicators.projectNPV;
            });
        });

        return { sensitivityData, tariffRange, landCostRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">NPV Sensitivity Analysis (Tariff vs. Land Cost)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Land Cost ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {landCostRange.map((landCost, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(landCost)}</th>
                                {sensitivityData[rowIndex].map((npv, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    const isNegative = npv < 0;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''} ${isNegative ? 'text-red-500' : ''}`}>
                                            {formatCurrency(npv)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// BESS Sensitivity Analysis Tables

const BessArbitrageSpreadSensitivityTable = ({ inputs }) => {
    const { sensitivityData, arbitrageSpreadRange, degradationRange } = useMemo(() => {
        const baseArbitrageSpread = inputs.bessArbitrageSpread;
        const baseDegradation = inputs.bessDegradationRate;

        const arbitrageSpreadRange = [-0.2, -0.1, 0, 0.1, 0.3].map(mod => baseArbitrageSpread * (1 + mod));
        const degradationRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseDegradation * (1 + mod));

        const sensitivityData = degradationRange.map(degradation => {
            return arbitrageSpreadRange.map(spread => {
                const tempInputs = { ...inputs, bessArbitrageSpread: spread, bessDegradationRate: degradation };
                const results = calculateFinancials(tempInputs);
                return results.indicators.projectNPV;
            });
        });

        return { sensitivityData, arbitrageSpreadRange, degradationRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Arbitrage Spread vs Degradation Rate (Project NPV)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Degradation ↓</span>
                                    <span className="font-normal text-xs">Arbitrage Spread →</span>
                                </div>
                            </th>
                            {arbitrageSpreadRange.map((spread, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatPercentage(spread)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(degradationRange[rowIndex])}
                                </td>
                                {row.map((npv, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${npv > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(npv, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BessAncillaryCapitalCostSensitivityTable = ({ inputs }) => {
    const { sensitivityData, ancillaryRateRange, capitalCostRange } = useMemo(() => {
        const baseAncillaryRate = inputs.bessAncillaryRate;
        const baseCapitalCost = inputs.bessCostPerMWh;

        const ancillaryRateRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseAncillaryRate * (1 + mod));
        const capitalCostRange = [-0.3, -0.15, 0, 0.15, 0.3].map(mod => baseCapitalCost * (1 + mod));

        const sensitivityData = capitalCostRange.map(cost => {
            return ancillaryRateRange.map(rate => {
                const tempInputs = { ...inputs, bessAncillaryRate: rate, bessCostPerMWh: cost };
                const results = calculateFinancials(tempInputs);
                const bessInvestment = results.investmentPlan?.bessInvestment || 0;
                const annualBessRevenue = results.bessStatement?.[0] ? 
                    (results.bessStatement[0].arbitrageRevenue + results.bessStatement[0].ancillaryRevenue + 
                     results.bessStatement[0].capacityRevenue + results.bessStatement[0].demandChargeReduction - 
                     results.bessStatement[0].operatingCost) : 0;
                return bessInvestment > 0 && annualBessRevenue > 0 ? bessInvestment / annualBessRevenue : Infinity;
            });
        });

        return { sensitivityData, ancillaryRateRange, capitalCostRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Ancillary Services vs Capital Cost (Payback Period - Years)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Capital Cost ↓</span>
                                    <span className="font-normal text-xs">Ancillary Rate →</span>
                                </div>
                            </th>
                            {ancillaryRateRange.map((rate, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatCurrency(rate, 0)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatCurrency(capitalCostRange[rowIndex], 0)}
                                </td>
                                {row.map((payback, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${payback < 15 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {payback === Infinity ? "Never" : payback.toFixed(1)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BessEfficiencyReplacementSensitivityTable = ({ inputs }) => {
    const { sensitivityData, efficiencyRange, replacementFactorRange } = useMemo(() => {
        const baseEfficiency = inputs.bessRoundTripEfficiency;
        const baseReplacementFactor = inputs.bessReplacementCostFactor;

        const efficiencyRange = [-0.1, -0.05, 0, 0.05, 0.1].map(mod => Math.max(0.7, Math.min(0.95, baseEfficiency * (1 + mod))));
        const replacementFactorRange = [-0.3, -0.15, 0, 0.15, 0.3].map(mod => baseReplacementFactor * (1 + mod));

        const sensitivityData = replacementFactorRange.map(factor => {
            return efficiencyRange.map(efficiency => {
                const tempInputs = { ...inputs, bessRoundTripEfficiency: efficiency, bessReplacementCostFactor: factor };
                const results = calculateFinancials(tempInputs);
                return results.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction - b.operatingCost - b.replacementCapex), 0) || 0;
            });
        });

        return { sensitivityData, efficiencyRange, replacementFactorRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Efficiency vs Replacement Cost Factor (Total BESS Cash Flow)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Replacement Factor ↓</span>
                                    <span className="font-normal text-xs">Efficiency →</span>
                                </div>
                            </th>
                            {efficiencyRange.map((efficiency, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatPercentage(efficiency)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(replacementFactorRange[rowIndex])}
                                </td>
                                {row.map((cashFlow, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${cashFlow > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(cashFlow, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BessReplacementPeriodSensitivityTable = ({ inputs }) => {
    const { sensitivityData, replacementPeriodRange, degradationRange } = useMemo(() => {
        const baseReplacementPeriod = inputs.bessReplacementPeriod;
        const baseDegradation = inputs.bessDegradationRate;

        const replacementPeriodRange = [-0.33, -0.17, 0, 0.17, 0.33].map(mod => Math.max(10, Math.min(20, Math.round(baseReplacementPeriod * (1 + mod)))));
        const degradationRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseDegradation * (1 + mod));

        const sensitivityData = degradationRange.map(degradation => {
            return replacementPeriodRange.map(period => {
                const tempInputs = { ...inputs, bessReplacementPeriod: period, bessDegradationRate: degradation };
                const results = calculateFinancials(tempInputs);
                const bessNPV = results.bessStatement?.reduce((acc, statement) => {
                    const revenue = statement.arbitrageRevenue + statement.ancillaryRevenue + statement.capacityRevenue + statement.demandChargeReduction;
                    const costs = statement.operatingCost + statement.replacementCapex;
                    const netCashFlow = revenue - costs;
                    return acc + netCashFlow / Math.pow(1 + inputs.discountRate, statement.year);
                }, -(results.investmentPlan?.bessInvestment || 0)) || 0;
                return bessNPV;
            });
        });

        return { sensitivityData, replacementPeriodRange, degradationRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Replacement Period vs Degradation Rate (BESS NPV)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Degradation ↓</span>
                                    <span className="font-normal text-xs">Replacement Period →</span>
                                </div>
                            </th>
                            {replacementPeriodRange.map((period, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{period} yrs</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(degradationRange[rowIndex])}
                                </td>
                                {row.map((npv, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${npv > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(npv, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BessTariffArbitrageSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, arbitrageSpreadRange, solarOnlyNPV } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseArbitrageSpread = inputs.bessArbitrageSpread;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const arbitrageSpreadRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => baseArbitrageSpread * (1 + mod));

        // Calculate solar-only NPV for comparison
        const solarOnlyInputs = { ...inputs, bessEnabled: false };
        const solarOnlyNPV = calculateFinancials(solarOnlyInputs).indicators.projectNPV;

        const sensitivityData = arbitrageSpreadRange.map(spread => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, bessArbitrageSpread: spread };
                const results = calculateFinancials(tempInputs);
                const bessNPV = results.indicators.projectNPV;
                const enhancement = bessNPV - solarOnlyNPV;
                return enhancement;
            });
        });

        return { sensitivityData, tariffRange, arbitrageSpreadRange, solarOnlyNPV };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Grid Tariff vs Arbitrage Spread (BESS Revenue Enhancement vs Solar-only)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Arbitrage Spread ↓</span>
                                    <span className="font-normal text-xs">Grid Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {formatPercentage(arbitrageSpreadRange[rowIndex])}
                                </td>
                                {row.map((enhancement, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${enhancement > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {formatCurrency(enhancement, 0)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BessSizingOptimizationTable = ({ inputs }) => {
    const { sensitivityData, energyCapacityRange, cyclesRange } = useMemo(() => {
        const baseEnergyCapacity = inputs.bessEnergyCapacity;
        const baseCycles = inputs.bessArbitrageCycles;

        const energyCapacityRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseEnergyCapacity * (1 + mod));
        const cyclesRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => Math.max(0.8, Math.min(2.0, baseCycles * (1 + mod))));

        const sensitivityData = cyclesRange.map(cycles => {
            return energyCapacityRange.map(capacity => {
                const tempInputs = { ...inputs, bessEnergyCapacity: capacity, bessArbitrageCycles: cycles };
                const results = calculateFinancials(tempInputs);
                const bessInvestment = results.investmentPlan?.bessInvestment || 0;
                const bessRevenue = results.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0;
                return bessInvestment > 0 ? bessRevenue / bessInvestment : 0;
            });
        });

        return { sensitivityData, energyCapacityRange, cyclesRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Energy Capacity vs Arbitrage Cycles (BESS Revenue per $ Invested)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Cycles/Day ↓</span>
                                    <span className="font-normal text-xs">Energy Capacity →</span>
                                </div>
                            </th>
                            {energyCapacityRange.map((capacity, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{capacity.toFixed(0)} MWh</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {cyclesRange[rowIndex].toFixed(1)}
                                </td>
                                {row.map((ratio, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${ratio > 0.3 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : ratio > 0.2 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {ratio.toFixed(3)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const BasicDataTab = ({ inputs, outputs, onChange, onGoalSeek, isLoading, goalSeekMessage }) => (
    <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Solar + BESS Financial Model - Basic Data & Assumptions</h2>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            <div>
                <SectionHeader title="Project Parameters" />
                <InputCell id="installedCapacity" label="Installed Capacity" value={inputs.installedCapacity} onChange={onChange} unit="MWp" />
                <InputCell id="investmentPerMW" label="Investment per MW" value={inputs.investmentPerMW} onChange={onChange} unit="USD/MWp" />
                <InputCell id="annualHours" label="Annual Utilization Hours" value={inputs.annualHours} onChange={onChange} unit="h" />
                <InputCell id="onGridTariff" label="On-grid Tariff (ex-tax)" value={inputs.onGridTariff} onChange={onChange} unit="USD/kWh" step="0.01" />
                                        <InputCell id="pvDegradationRate" label="PV Degradation / Year" value={inputs.pvDegradationRate} onChange={onChange} isPercentage unit="%" step="0.1" />
                <InputCell id="constructionPeriod" label="Construction Period" value={inputs.constructionPeriod} onChange={onChange} unit="year" />
                <InputCell id="operationPeriod" label="Operation Period" value={inputs.operationPeriod} onChange={onChange} unit="year" />
                <InputCell id="landCostPerAcre" label="Land Cost per Acre (annual)" value={inputs.landCostPerAcre} onChange={onChange} unit="USD/acre/year" />
                                        <InputCell id="acresPerMW" label="Acres per MW" value={inputs.acresPerMW} onChange={onChange} unit="acres/MW" step="0.1" />
                <OutputCell label="Total Acres Required" value={inputs.installedCapacity * inputs.acresPerMW} unit="acres" formatter={(v) => v.toFixed(1)} />
                <OutputCell label="Annual Land Cost" value={inputs.installedCapacity * inputs.acresPerMW * inputs.landCostPerAcre} formatter={formatCurrency} />
            </div>
            <div>
                <SectionHeader title="Financing & Capital" />
                <InputCell id="ownFunds" label="Own Funds" value={inputs.ownFunds} onChange={onChange} unit="USD" />
                                        <InputCell id="longTermLoanRate" label="Loan Interest Rate" value={inputs.longTermLoanRate} onChange={onChange} isPercentage unit="%" step="0.1" />
                <OutputCell label="Capital Fund Ratio" value={outputs.investmentPlan?.capitalFundRatio} formatter={formatPercentage} />
                <OutputCell label="Total Investment" value={outputs.investmentPlan?.totalInvestment} unit="USD" />
                                        <InputCell id="discountRate" label="Discount Rate (WACC)" value={inputs.discountRate} onChange={onChange} isPercentage unit="%" step="0.1" />
            </div>
            <div>
                <SectionHeader title="Costs & Depreciation" />
                                        <InputCell id="operatingCostRate" label="Operating Cost Rate" value={inputs.operatingCostRate} onChange={onChange} isPercentage unit="% of revenue" step="0.1" />
                        <InputCell id="operatingCostEscalationRate" label="Op. Cost Escalation Rate" value={inputs.operatingCostEscalationRate} onChange={onChange} isPercentage unit="%" step="0.1" />
                <InputCell id="depreciationPeriod" label="Depreciation Period" value={inputs.depreciationPeriod} onChange={onChange} unit="year" />
                                        <InputCell id="salvageValueRate" label="Salvage Value Rate" value={inputs.salvageValueRate} onChange={onChange} isPercentage unit="%" step="1" />
            </div>
            <div>
                <SectionHeader title="Tax & Profit Distribution" />
                                        <InputCell id="vatRate" label="VAT Rate" value={inputs.vatRate} onChange={onChange} isPercentage unit="%" step="1" />
                        <InputCell id="surtaxRate" label="Surtax Rate (of VAT)" value={inputs.surtaxRate} onChange={onChange} isPercentage unit="%" step="1" />
                        <InputCell id="incomeTaxRate" label="Income Tax Rate" value={inputs.incomeTaxRate} onChange={onChange} isPercentage unit="%" step="1" />
                        <InputCell id="statutoryReserveRate" label="Statutory Reserve" value={inputs.statutoryReserveRate} onChange={onChange} isPercentage unit="%" step="1" />
                        <InputCell id="discretionaryReserveRate" label="Discretionary Reserve" value={inputs.discretionaryReserveRate} onChange={onChange} isPercentage unit="%" step="1" />
                        <InputCell id="profitDistributionRate" label="Profit Distribution" value={inputs.profitDistributionRate} onChange={onChange} isPercentage unit="%" step="1" />
            </div>
            <div className="md:col-span-2">
                <SectionHeader title="Battery Energy Storage System (BESS)" />
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                    <div className="flex items-center mb-4">
                        <Battery className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                        <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">BESS Configuration</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <CheckboxCell 
                                id="bessEnabled" 
                                checked={inputs.bessEnabled} 
                                onChange={onChange} 
                                label="Enable BESS" 
                                description="Add battery energy storage system to solar project"
                            />
                            {inputs.bessEnabled && (
                                <>
                                    <InputCell id="bessEnergyCapacity" label="Energy Capacity" value={inputs.bessEnergyCapacity} onChange={onChange} unit="MWh" />
                                    <InputCell id="bessPowerCapacity" label="Power Capacity" value={inputs.bessPowerCapacity} onChange={onChange} unit="MW" />
                                    <InputCell id="bessCostPerMWh" label="Energy Cost" value={inputs.bessCostPerMWh} onChange={onChange} unit="USD/MWh" />
                                    <InputCell id="bessPowerCostPerMW" label="Power Cost" value={inputs.bessPowerCostPerMW} onChange={onChange} unit="USD/MW" />
                                    <InputCell id="bessRoundTripEfficiency" label="Round-trip Efficiency" value={inputs.bessRoundTripEfficiency} onChange={onChange} isPercentage unit="%" step="1" />
                                    <InputCell id="bessDegradationRate" label="Annual Degradation" value={inputs.bessDegradationRate} onChange={onChange} isPercentage unit="%" step="0.1" />
                                    <InputCell id="bessOperatingCostRate" label="Operating Cost" value={inputs.bessOperatingCostRate} onChange={onChange} unit="USD/MW/year" />
                                    <InputCell id="bessReplacementPeriod" label="Replacement Period" value={inputs.bessReplacementPeriod} onChange={onChange} unit="years" />
                                    <InputCell id="bessReplacementCostFactor" label="Replacement Cost Factor" value={inputs.bessReplacementCostFactor} onChange={onChange} isPercentage unit="%" step="1" />
                                </>
                            )}
                        </div>
                        {inputs.bessEnabled && (
                            <div>
                                <h4 className="text-md font-semibold text-purple-800 dark:text-purple-200 mb-3">Revenue Streams</h4>
                                <InputCell id="bessArbitrageCycles" label="Arbitrage Cycles/Day" value={inputs.bessArbitrageCycles} onChange={onChange} unit="cycles" />
                                <InputCell id="bessArbitrageSpread" label="Arbitrage Spread" value={inputs.bessArbitrageSpread} onChange={onChange} isPercentage unit="%" step="1" />
                                <InputCell id="bessAncillaryRate" label="Ancillary Services" value={inputs.bessAncillaryRate} onChange={onChange} unit="USD/MW/year" />
                                <InputCell id="bessCapacityRate" label="Capacity Revenue" value={inputs.bessCapacityRate} onChange={onChange} unit="USD/MW/month" />
                                <InputCell id="bessDemandChargeReduction" label="Demand Charge Reduction" value={inputs.bessDemandChargeReduction} onChange={onChange} unit="USD/MW/month" />
                                {inputs.bessEnabled && (
                                    <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
                                        <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2">BESS Investment Summary</h5>
                                        <div className="text-xs space-y-1">
                                            <div className="flex justify-between">
                                                <span>Energy Investment:</span>
                                                <span className="font-semibold">{formatCurrency(inputs.bessEnergyCapacity * inputs.bessCostPerMWh)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Power Investment:</span>
                                                <span className="font-semibold">{formatCurrency(inputs.bessPowerCapacity * inputs.bessPowerCostPerMW)}</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-1">
                                                <span>Total BESS Investment:</span>
                                                <span className="font-bold text-purple-600 dark:text-purple-400">{formatCurrency(outputs.investmentPlan?.bessInvestment || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="md:col-span-2">
                <SectionHeader title="Valuation & Goal Seeking" />
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-blue-50 dark:bg-gray-800/50">
                     <div className="grid md:grid-cols-3 gap-4 items-center">
                        <InputCell id="goalSeekTargetIRR" label="Target Project IRR" value={inputs.goalSeekTargetIRR} onChange={onChange} isPercentage unit="%" step="0.1" />
                        <OutputCell label="Current Project IRR" value={outputs.indicators?.projectIRR} formatter={formatPercentage} />
                        <button onClick={onGoalSeek} disabled={isLoading} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 h-10">
                            {isLoading ? 'Calculating...' : 'Find Required Own Funds'}
                        </button>
                    </div>
                    {goalSeekMessage && <p className={`text-sm mt-2 font-medium ${goalSeekMessage.startsWith('Target') ? 'text-red-600' : 'text-green-600'}`}>{goalSeekMessage}</p>}
                </div>
                <NpvSensitivityTable inputs={inputs} />
                <CapitalIrrSensitivityTable inputs={inputs} />
                <ProjectIrrSensitivityTable inputs={inputs} />
                <LandCostSensitivityTable inputs={inputs} />
                
                {/* BESS Sensitivity Analysis Tables - Only shown when BESS is enabled */}
                {inputs.bessEnabled && (
                    <div className="mt-8 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                        <h3 className="text-lg font-bold text-purple-800 dark:text-purple-200 mb-4">🔋 BESS Sensitivity Analysis</h3>
                        <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
                            Battery energy storage systems introduce additional risk factors that require specialized sensitivity analysis. 
                            These tables help evaluate critical BESS investment decisions and risk exposure.
                        </p>
                        <BessArbitrageSpreadSensitivityTable inputs={inputs} />
                        <BessAncillaryCapitalCostSensitivityTable inputs={inputs} />
                        <BessEfficiencyReplacementSensitivityTable inputs={inputs} />
                        <BessReplacementPeriodSensitivityTable inputs={inputs} />
                        <BessTariffArbitrageSensitivityTable inputs={inputs} />
                        <BessSizingOptimizationTable inputs={inputs} />
                    </div>
                )}
            </div>
        </div>
        <Explanation title="How This Works">
            <p>This tab is the control panel for the entire financial model. All cells with a white background are inputs that you can change to see their impact on the project's financials.</p>
            <p><strong>Land Cost Integration:</strong> The model now includes land costs as an annual operating expense. Based on your input for "Land Cost per Acre" and "Acres per MW", the model calculates the total land area needed and the annual land lease cost. This affects all financial metrics including LCOE, NPV, and IRR. The default assumption is 5 acres per MW, which is typical for utility-scale solar projects.</p>
            <p><strong>Principle:</strong> The core principle here is sensitivity analysis. By changing these base assumptions (like the cost of investment, the price of electricity, interest rates, or land costs), you can model different scenarios (e.g., best-case, worst-case, expected-case) to understand the project's risks and opportunities.</p>
            <p><strong>Goal:</strong> The goal is to provide a single place to define all the variables that drive the model. Any change here will automatically flow through to all other tabs, recalculating every financial statement in real-time.</p>
            
            {inputs.bessEnabled && (
                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">🔋 Priority BESS Sensitivity Analysis Framework</h5>
                    
                    <div className="mt-4">
                        <h6 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">1. High-Impact Revenue Sensitivities</h6>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">A. Arbitrage Spread vs BESS Degradation Rate</strong>
                                <p><strong>Why Critical:</strong> Arbitrage spread is the most volatile revenue stream (market-dependent), and degradation affects all energy-based revenues</p>
                                <p><strong>Ranges:</strong> Arbitrage spread ±50% (-20% to +30%), Degradation rate ±40% (1.0% to 2.5%/year)</p>
                                <p><strong>Output Metric:</strong> Project NPV or BESS NPV contribution</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">B. Ancillary Services Rate vs BESS Capital Cost ($/MWh)</strong>
                                <p><strong>Why Critical:</strong> Ancillary rates are regulatory/policy dependent, and battery costs are declining rapidly but uncertain</p>
                                <p><strong>Ranges:</strong> Ancillary rates ±40%, Capital costs ±30%</p>
                                <p><strong>Output Metric:</strong> BESS Payback Period</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h6 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">2. Technology Risk Sensitivities</h6>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">C. Round-trip Efficiency vs Replacement Cost Factor</strong>
                                <p><strong>Why Critical:</strong> Efficiency affects all energy revenues; replacement costs depend on technology learning curves</p>
                                <p><strong>Ranges:</strong> Efficiency ±10% (80%-95%), Replacement factor ±30% (40%-70% of original cost)</p>
                                <p><strong>Output Metric:</strong> Total BESS Cash Flow</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">D. Replacement Period vs Battery Degradation Rate</strong>
                                <p><strong>Why Critical:</strong> Both affect replacement timing and long-term economics</p>
                                <p><strong>Ranges:</strong> Replacement period ±33% (10-20 years), Degradation ±40%</p>
                                <p><strong>Output Metric:</strong> NPV of BESS over project life</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h6 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">3. Market/Financial Sensitivities</h6>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">E. Grid Tariff vs Arbitrage Spread</strong>
                                <p><strong>Why Critical:</strong> Shows how BESS value changes with overall electricity prices</p>
                                <p><strong>Ranges:</strong> Grid tariff ±20%, Arbitrage spread ±50%</p>
                                <p><strong>Output Metric:</strong> BESS Revenue Enhancement vs Solar-only</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">F. BESS Energy Capacity vs Arbitrage Cycles/Day</strong>
                                <p><strong>Why Critical:</strong> Sizing optimization - bigger isn't always better</p>
                                <p><strong>Ranges:</strong> Energy capacity ±40%, Cycles ±50% (0.8-2.0 cycles/day)</p>
                                <p><strong>Output Metric:</strong> BESS Revenue per $ Invested</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h6 className="font-semibold text-green-700 dark:text-green-300 mb-2">🎯 BESS Decision Framework</h6>
                        <p className="text-sm">The sensitivity analysis should help answer:</p>
                        <div className="ml-4 space-y-1 text-sm">
                            <p><strong>Threshold Analysis:</strong> At what arbitrage spread does BESS become uneconomical?</p>
                            <p><strong>Break-even Points:</strong> How high can degradation rates go before replacement economics fail?</p>
                            <p><strong>Sizing Optimization:</strong> What's the optimal energy/power ratio for different market conditions?</p>
                            <p><strong>Technology Risk:</strong> How sensitive is the project to battery technology assumptions?</p>
                        </div>
                    </div>
                </div>
            )}
        </Explanation>
    </div>
);

const Table = ({ headers, keys, data, formatters, boldRows = [] }) => (<div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead><tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">{data.map((row, rowIndex) => (<tr key={rowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${boldRows.includes(rowIndex) ? 'font-bold' : ''}`}>{keys.map(key => (<td key={key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatters[key] ? formatters[key](row[key]) : row[key]}</td>))}</tr>))}</tbody></table></div>);

const BessTab = ({ outputs, inputs }) => {
    if (!inputs.bessEnabled) {
        return (
            <div className="text-center py-12">
                <Battery className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">BESS Not Enabled</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                    To view BESS analysis, please enable Battery Energy Storage System in the Basic Data tab.
                </p>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                <Battery className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
                Battery Energy Storage System Analysis
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-lg mb-3 text-blue-700 dark:text-blue-400">BESS Configuration Summary</h3>
                    <OutputCell label="Energy Capacity" value={inputs.bessEnergyCapacity} unit="MWh" formatter={(v) => `${v} MWh`} />
                    <OutputCell label="Power Capacity" value={inputs.bessPowerCapacity} unit="MW" formatter={(v) => `${v} MW`} />
                    <OutputCell label="Energy/Power Ratio" value={inputs.bessEnergyCapacity / inputs.bessPowerCapacity} unit="hours" formatter={(v) => `${v.toFixed(1)} hours`} />
                    <OutputCell label="Round-trip Efficiency" value={inputs.bessRoundTripEfficiency} formatter={formatPercentage} />
                    <OutputCell label="Annual Degradation" value={inputs.bessDegradationRate} formatter={formatPercentage} />
                    <OutputCell label="Replacement Period" value={inputs.bessReplacementPeriod} unit="years" formatter={(v) => `${v} years`} />
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-lg mb-3 text-green-700 dark:text-green-400">BESS Investment Breakdown</h3>
                    <OutputCell label="Energy Investment" value={inputs.bessEnergyCapacity * inputs.bessCostPerMWh} />
                    <OutputCell label="Power Investment" value={inputs.bessPowerCapacity * inputs.bessPowerCostPerMW} />
                    <OutputCell label="Total BESS Investment" value={outputs.investmentPlan?.bessInvestment || 0} />
                    <OutputCell label="BESS % of Total Project" value={(outputs.investmentPlan?.bessInvestment || 0) / (outputs.investmentPlan?.totalInvestment || 1)} formatter={formatPercentage} />
                    <OutputCell label="Annual Operating Cost" value={inputs.bessPowerCapacity * inputs.bessOperatingCostRate} />
                </div>
            </div>

            <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">BESS Revenue Streams Analysis</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {outputs.bessStatement && outputs.bessStatement[0] && (
                        <>
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
                                <h4 className="font-semibold text-blue-800 dark:text-blue-200">Energy Arbitrage</h4>
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(outputs.bessStatement[0].arbitrageRevenue, 0)}</p>
                                <p className="text-sm text-blue-700 dark:text-blue-300">Year 1 Revenue</p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-600">
                                <h4 className="font-semibold text-green-800 dark:text-green-200">Ancillary Services</h4>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(outputs.bessStatement[0].ancillaryRevenue, 0)}</p>
                                <p className="text-sm text-green-700 dark:text-green-300">Year 1 Revenue</p>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                                <h4 className="font-semibold text-purple-800 dark:text-purple-200">Capacity Revenue</h4>
                                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(outputs.bessStatement[0].capacityRevenue, 0)}</p>
                                <p className="text-sm text-purple-700 dark:text-purple-300">Year 1 Revenue</p>
                            </div>
                            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-600">
                                <h4 className="font-semibold text-orange-800 dark:text-orange-200">Demand Reduction</h4>
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(outputs.bessStatement[0].demandChargeReduction, 0)}</p>
                                <p className="text-sm text-orange-700 dark:text-orange-300">Year 1 Revenue</p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">BESS Performance Over Time</h3>
            <Table 
                headers={['Year', 'Energy Capacity (MWh)', 'Arbitrage Revenue', 'Ancillary Revenue', 'Capacity Revenue', 'Demand Reduction', 'Operating Cost', 'Replacement CapEx', 'BESS Depreciation']} 
                keys={['year', 'energyCapacity', 'arbitrageRevenue', 'ancillaryRevenue', 'capacityRevenue', 'demandChargeReduction', 'operatingCost', 'replacementCapex', 'bessDepreciation']} 
                data={outputs.bessStatement || []} 
                formatters={{
                    'energyCapacity': (v) => v.toFixed(2),
                    'arbitrageRevenue': formatCurrency, 
                    'ancillaryRevenue': formatCurrency, 
                    'capacityRevenue': formatCurrency, 
                    'demandChargeReduction': formatCurrency, 
                    'operatingCost': formatCurrency, 
                    'replacementCapex': formatCurrency,
                    'bessDepreciation': formatCurrency
                }} 
            />
            
            <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔋 BESS Financial Impact</h3>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="font-semibold text-lg mb-3 text-blue-700 dark:text-blue-400">Revenue Enhancement</h4>
                        <div className="space-y-2">
                            <OutputCell label="Total BESS Revenue (25 years)" value={outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0} />
                            <OutputCell label="Average Annual BESS Revenue" value={(outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0) / inputs.operationPeriod} />
                        </div>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="font-semibold text-lg mb-3 text-red-700 dark:text-red-400">Cost Structure</h4>
                        <div className="space-y-2">
                            <OutputCell label="Total BESS Operating Costs" value={outputs.bessStatement?.reduce((a,b) => a + b.operatingCost, 0) || 0} />
                            <OutputCell label="Total Replacement CapEx" value={outputs.bessStatement?.reduce((a,b) => a + b.replacementCapex, 0) || 0} />
                            <OutputCell label="Total BESS Depreciation" value={outputs.bessStatement?.reduce((a,b) => a + b.bessDepreciation, 0) || 0} />
                        </div>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="font-semibold text-lg mb-3 text-green-700 dark:text-green-400">Net Benefit</h4>
                        <div className="space-y-2">
                            <OutputCell label="Net BESS Cash Flow" value={(outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction - b.operatingCost - b.replacementCapex), 0) || 0)} />
                            <OutputCell label="BESS Accounting Profit" value={(outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction - b.operatingCost - b.bessDepreciation), 0) || 0)} />
                            <OutputCell label="BESS Payback Period" value={(() => {
                                const investment = outputs.investmentPlan?.bessInvestment || 0;
                                const annualCashFlow = ((outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction - b.operatingCost), 0) || 0) / (outputs.bessStatement?.length || 1));
                                return annualCashFlow > 0 ? investment / annualCashFlow : Infinity;
                            })()} formatter={(v) => v === Infinity ? "Never" : formatYears(v)} />
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
                <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200 mb-4">💡 BESS Value Proposition</h3>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">Revenue Diversification</h4>
                        <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                            <li>• Energy arbitrage leveraging price spreads</li>
                            <li>• Ancillary services for grid stability</li>
                            <li>• Capacity market participation</li>
                            <li>• Demand charge reduction for customers</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-2">System Benefits</h4>
                        <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-1">
                            <li>• Enhanced project economics</li>
                            <li>• Improved grid integration</li>
                            <li>• Increased capacity factor utilization</li>
                            <li>• Risk mitigation through diversification</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <Explanation title="How This Works">
                <p>The BESS Analysis combines battery storage with solar generation to unlock multiple revenue streams and enhance project economics. This section breaks down every calculation and relationship so you can understand exactly how battery storage adds value to your solar project.</p>
                
                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">🔋 BESS Configuration & Investment</h5>
                    <p><strong>Energy vs Power Capacity:</strong> Think of energy capacity (MWh) as the "fuel tank size" - how much electricity the battery can store. Power capacity (MW) is the "engine size" - how fast it can charge or discharge. The ratio between them (hours) tells you how long the battery can run at full power.</p>
                    <p><strong>Investment Calculation:</strong> Total BESS investment = (Energy Capacity × Cost per MWh) + (Power Capacity × Cost per MW). For example: (100 MWh × $280,000/MWh) + (50 MW × $180,000/MW) = $37M total.</p>
                    <p><strong>Round-trip Efficiency:</strong> This accounts for energy losses during charging and discharging. An 87% efficiency means you lose 13% of stored energy due to conversion losses, heat, and other inefficiencies.</p>
                </div>

                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">💰 Revenue Stream Calculations</h5>
                    
                    <div className="ml-4 space-y-4">
                        <div>
                            <h6 className="font-semibold text-blue-600 dark:text-blue-400">🔄 Energy Arbitrage (Buy Low, Sell High)</h6>
                            <p><strong>How it works:</strong> The battery stores cheap electricity during off-peak hours and sells it back to the grid when prices are high.</p>
                            <p><strong>Calculation:</strong> Energy Capacity × Cycles per Day × 365 days × Price Spread × Round-trip Efficiency</p>
                            <p><strong>Example:</strong> 100 MWh × 1.2 cycles/day × 365 × ($0.035 spread) × 87% = $1.33M annually</p>
                            <p><strong>Why it degrades:</strong> As battery capacity decreases over time due to degradation, less energy can be stored and arbitraged.</p>
                        </div>
                        
                        <div>
                            <h6 className="font-semibold text-green-600 dark:text-green-400">⚡ Ancillary Services (Grid Stability)</h6>
                            <p><strong>How it works:</strong> The grid operator pays for the battery's ability to provide frequency regulation, voltage support, and other grid stabilization services.</p>
                            <p><strong>Calculation:</strong> Power Capacity × Ancillary Rate per MW per year</p>
                            <p><strong>Example:</strong> 50 MW × $28,000/MW/year = $1.4M annually</p>
                            <p><strong>Why it degrades:</strong> Grid services depend on the battery's responsiveness, which decreases as the system ages.</p>
                        </div>
                        
                        <div>
                            <h6 className="font-semibold text-purple-600 dark:text-purple-400">🏭 Capacity Revenue (Grid Reserve)</h6>
                            <p><strong>How it works:</strong> The grid operator pays for the battery's availability as backup power, whether it's actually used or not - like paying for insurance.</p>
                            <p><strong>Calculation:</strong> Power Capacity × Capacity Rate per MW per month × 12 months</p>
                            <p><strong>Example:</strong> 50 MW × $6,000/MW/month × 12 = $3.6M annually</p>
                            <p><strong>Why it doesn't degrade:</strong> This payment is for power availability (MW), not energy storage (MWh), so degradation has minimal impact.</p>
                        </div>
                        
                        <div>
                            <h6 className="font-semibold text-orange-600 dark:text-orange-400">📉 Demand Charge Reduction (Peak Shaving)</h6>
                            <p><strong>How it works:</strong> The battery reduces peak demand charges for customers by discharging during their highest consumption periods, lowering their utility bills.</p>
                            <p><strong>Calculation:</strong> Power Capacity × Demand Reduction Value per MW per month × 12 months</p>
                            <p><strong>Example:</strong> 50 MW × $9,500/MW/month × 12 = $5.7M annually</p>
                            <p><strong>Why it doesn't degrade:</strong> Peak shaving is about power output (MW) capacity, which remains relatively stable compared to energy storage.</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">📊 Performance Modeling Over Time</h5>
                    <p><strong>Annual Degradation:</strong> Battery capacity decreases by the degradation rate each year (e.g., 1.8%/year). After 10 years at 1.8% degradation, capacity is about 83% of original.</p>
                    <p><strong>Replacement Strategy:</strong> When performance drops significantly (typically after 15 years), batteries are replaced at 55% of original cost due to technology improvements and learning curves.</p>
                    <p><strong>Depreciation Accounting:</strong> Both initial and replacement batteries are capitalized as assets and depreciated over their useful life, rather than expensed immediately. This provides more accurate accounting and smoother profit recognition.</p>
                </div>

                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">💡 Financial Impact & Metrics</h5>
                    <p><strong>Cash Flow vs Accounting Profit:</strong> Cash flow subtracts actual capital expenditures for replacements, while accounting profit uses depreciation. Cash flow is what investors actually receive, while accounting profit smooths out replacement costs.</p>
                    <p><strong>Payback Period:</strong> How long it takes for cumulative net cash flows (revenues minus operating costs) to equal the initial BESS investment. Shorter payback periods indicate more attractive investments.</p>
                    <p><strong>Total Project Impact:</strong> BESS adds both to the investment (increasing the denominator) and revenue (increasing the numerator) when calculating project returns like IRR and NPV.</p>
                </div>

                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">🔗 Key Relationships & Dependencies</h5>
                    <p><strong>Solar-BESS Synergy:</strong> Battery storage allows solar projects to shift energy sales to peak-price periods, increasing overall project revenue beyond simple solar generation.</p>
                    <p><strong>Grid Integration Value:</strong> BESS helps solar projects provide grid services that pure solar cannot, accessing additional revenue markets and increasing project competitiveness.</p>
                    <p><strong>Risk Diversification:</strong> Multiple revenue streams reduce project risk. If one revenue source declines (e.g., arbitrage spreads narrow), others (capacity payments, ancillary services) can maintain project viability.</p>
                    <p><strong>Scale Economics:</strong> Larger BESS installations often achieve better economics due to fixed cost spreading and improved bargaining power for revenue contracts.</p>
                </div>

                <div className="mt-6">
                    <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">🎯 Strategic Decision Framework</h5>
                    <p><strong>When BESS Makes Sense:</strong> High electricity price volatility, substantial grid service payments, significant demand charges, or regulatory requirements for storage increase BESS value.</p>
                    <p><strong>Size Optimization:</strong> The optimal BESS size balances investment costs against revenue potential. Oversizing may not generate proportional returns, while undersizing misses revenue opportunities.</p>
                    <p><strong>Technology Selection:</strong> Different battery technologies (lithium-ion, flow batteries, etc.) have different cost, performance, and degradation characteristics that affect long-term economics.</p>
                    <p><strong>Market Evolution:</strong> BESS economics continue improving due to falling costs, new revenue mechanisms, and increasing grid stability requirements, making storage increasingly attractive for solar projects.</p>
                </div>
            </Explanation>
        </div>
    );
};

const InvestmentFinancingTab = ({ outputs }) => {
    const planData = outputs.investmentPlan ? [
        { item: 'Solar Investment', value: outputs.investmentPlan.solarInvestment}, 
        ...(outputs.investmentPlan.bessInvestment > 0 ? [{ item: 'BESS Investment', value: outputs.investmentPlan.bessInvestment}] : []),
        { item: 'Total Static Investment', value: outputs.investmentPlan.totalStaticInvestment}, 
        { item: 'Interest During Construction', value: outputs.investmentPlan.interestDuringConstruction }, 
        { item: 'Working Capital', value: outputs.investmentPlan.workingCapital }, 
        { item: 'Total Investment', value: outputs.investmentPlan.totalInvestment}
    ] : [];
    const financingData = outputs.financing ? [{ item: 'Capital (Own Funds)', value: outputs.financing.capital }, { item: 'Loan', value: outputs.financing.loan }, { item: 'Total Financing', value: outputs.financing.totalFinancing}] : [];
    return (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Investment Plan & Financing</h2><div className="grid md:grid-cols-2 gap-8"><div><h3 className="text-lg font-semibold mb-2">Investment Plan</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{planData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div><div><h3 className="text-lg font-semibold mb-2">Financing Sources</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{financingData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div></div>
        <Explanation title="How This Works">
            <p>This tab breaks down the total cost of the project (Uses of Funds) and how it will be paid for (Sources of Funds).</p>
            <p><strong>Principle:</strong> The fundamental accounting equation is at play here: Assets = Liabilities + Equity. In this context, the Total Investment (the assets being created) must equal the Total Financing (the liabilities/loan + equity/own funds used to pay for them).</p>
            <p><strong>Goal:</strong> To clearly show the total capital required for the project and the mix of debt and equity that will be used. This is critical for understanding the project's capital structure and leverage.</p>
        </Explanation>
    </div>);
};

const LoanRepaymentTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Loan Repayment Schedule</h2><Table headers={['Year', 'Principal', 'Interest', 'Total Payment', 'Remaining Loan']} keys={['year', 'principal', 'interest', 'totalPayment', 'remainingLoan']} data={outputs.loanRepaymentSchedule || []} formatters={{'principal': formatCurrency, 'interest': formatCurrency, 'totalPayment': formatCurrency, 'remainingLoan': formatCurrency}} />
    <Explanation title="How This Works">
        <p>This tab details the year-by-year repayment of the loan taken out to finance the project.</p>
        <p><strong>Principle:</strong> This is a standard loan amortization schedule. Each year, a payment is made that consists of two parts: the principal (which reduces the loan balance) and the interest (the cost of borrowing). This model uses an equal principal repayment method, where the same amount of principal is paid back each year.</p>
        <p><strong>Goal:</strong> To calculate the annual interest and principal payments. The interest expense flows into the Profit & Loss statement, affecting profitability. Both principal and interest payments are cash outflows that impact the Balance Sheet and Cash Flow calculations.</p>
    </Explanation>
</div>);

const TotalCostTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Total Cost & Cost per kWh</h2><Table headers={['Year', 'Operating Cost', 'Land Cost', 'Solar Depreciation', 'BESS Depreciation', 'BESS O&M', 'Interest', 'Total Cost', 'Cost per kWh']} keys={['year', 'operatingCost', 'landCost', 'depreciation', 'bessDepreciation', 'bessOperatingCost', 'interest', 'totalCost', 'costPerKwh']} data={outputs.costStatement || []} formatters={{'operatingCost': formatCurrency, 'landCost': formatCurrency, 'depreciation': formatCurrency, 'bessDepreciation': formatCurrency, 'bessOperatingCost': formatCurrency, 'interest': formatCurrency, 'totalCost': formatCurrency, 'costPerKwh': (v) => formatCurrency(v, 4)}} />
    <Explanation title="How This Works">
        <p>This statement breaks down all the costs associated with running the solar plant each year, with proper accounting treatment for BESS replacements.</p>
        <p><strong>Principle:</strong> It separates costs into different categories. <strong>Operating Costs</strong> are the day-to-day expenses. <strong>Land Cost</strong> is the annual lease/rent for the land required by the solar farm. <strong>Solar Depreciation</strong> spreads the solar equipment cost over its useful life. <strong>BESS Depreciation</strong> handles multiple battery assets with staggered replacement schedules - each replacement is capitalized and depreciated over 15 years rather than expensed immediately. <strong>BESS O&M</strong> covers pure operating costs for battery maintenance. <strong>Interest</strong> is the cost of the project's debt. The "Cost per kWh" shows the all-in cost to produce one unit of electricity.</p>
        <p><strong>Goal:</strong> To provide accurate cost accounting that reflects the economic reality of long-term assets. BESS replacements are now properly treated as capital expenditures with depreciation schedules, avoiding artificial profit spikes in replacement years.</p>
    </Explanation>
</div>);

const ProfitLossTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Profit and Loss Statement</h2><Table headers={['Year', 'Operating Income', 'VAT & Surtax', 'Total Profit', 'Income Tax', 'Net Profit', 'Profit Distribution', 'Retained Earnings']} keys={['year', 'operatingIncome', 'vatSurtax', 'totalProfit', 'incomeTax', 'netProfit', 'profitDistribution', 'retainedEarnings']} data={outputs.pnlStatement || []} formatters={{'operatingIncome': formatCurrency, 'vatSurtax': formatCurrency, 'totalProfit': formatCurrency, 'incomeTax': formatCurrency, 'netProfit': formatCurrency, 'profitDistribution': formatCurrency, 'retainedEarnings': formatCurrency}} />
    <Explanation title="How This Works">
        <p>The Profit & Loss (P&L), or Income Statement, shows the project's financial performance over a period of time.</p>
        <p><strong>Principle:</strong> It follows the basic formula: Revenue - Expenses = Profit. It starts with Operating Income (revenue from selling electricity), subtracts all costs (from the Total Cost tab) and taxes, to arrive at the Net Profit (the "bottom line").</p>
        <p><strong>Goal:</strong> To determine if the project is profitable. The P&L also shows how much profit is available to be distributed to investors as dividends and how much is retained in the business to support future growth.</p>
    </Explanation>
</div>);

const BalanceSheetTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Balance Sheet</h2><Table headers={['Year', 'Total Assets', 'Total Liabilities', 'Total Equity', 'Total Liab. & Equity', 'Check']} keys={['year', 'totalAssets', 'totalLiabilities', 'totalEquity', 'totalLiabilitiesAndEquity', 'check']} data={outputs.balanceSheet || []} formatters={{'totalAssets': formatCurrency, 'totalLiabilities': formatCurrency, 'totalEquity': formatCurrency, 'totalLiabilitiesAndEquity': formatCurrency, 'check': (v) => formatCurrency(v, 0)}} />
    <Explanation title="How This Works">
        <p>The Balance Sheet provides a snapshot of the project's financial position at a single point in time (the end of each year).</p>
        <p><strong>Principle:</strong> It is built on the fundamental accounting equation: <strong>Assets = Liabilities + Equity</strong>. Assets are what the company owns (the solar plant, cash). Liabilities are what it owes (loans). Equity is the value belonging to the owners. The "Check" column should always be zero, ensuring the model is balanced.</p>
        <p><strong>Goal:</strong> To show the company's net worth and how its assets are financed. It provides a clear picture of the project's financial health and structure year over year.</p>
    </Explanation>
</div>);

const EVATab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Economic Value Added (EVA)</h2><Table headers={['Year', 'EVA']} keys={['year', 'eva']} data={outputs.evaStatement || []} formatters={{ 'eva': formatCurrency }} />
    <Explanation title="How This Works">
        <p>Economic Value Added (EVA) is a measure of a company's financial performance based on the residual wealth calculated by deducting its cost of capital from its operating profit.</p>
        <p><strong>Principle:</strong> The formula is EVA = NOPAT - (Capital Employed * WACC), where NOPAT is Net Operating Profit After Tax and WACC is the Weighted Average Cost of Capital (represented by our Discount Rate). A positive EVA means the project is generating returns in excess of its cost of capital, thus creating value. A negative EVA means it is destroying value.</p>
        <p><strong>Goal:</strong> To provide a performance metric that is more comprehensive than simple accounting profit, as it explicitly accounts for the cost of the capital used to generate that profit.</p>
    </Explanation>
</div>);

const ReadmeTab = ({ outputs, inputs, isDarkMode }) => {
    const [activeSection, setActiveSection] = useState('overview');
    
    // Generate cash flow data from actual financial statements
    const cashFlowData = useMemo(() => {
        if (!outputs.pnlStatement?.length || !outputs.balanceSheet?.length) {
            return [];
        }

        const data = [];
        
        // Year 0: Initial investment outflows
        data.push({
            year: 0,
            projectCF: -(outputs.investmentPlan?.totalInvestment || 0),
            capitalCF: -(inputs.ownFunds || 0),
            explanation: "Initial investment: Project CF shows total investment, Capital CF shows equity contribution"
        });

        // Operating years: Calculate cash flows from P&L and balance sheet
        outputs.pnlStatement.forEach((pnl, index) => {
            const balance = outputs.balanceSheet[index];
            const loanPayment = outputs.loanRepaymentSchedule?.[index];
            
            // Project Cash Flow = Operating Income + Depreciation (non-cash) - Capital Expenditures
            const depreciation = pnl.depreciation || 0;
            const bessReplacementCost = balance?.bessReplacementCosts || 0;
            const projectCashFlow = pnl.operatingIncome + depreciation - bessReplacementCost;
            
            // Capital Cash Flow = Project Cash Flow - Debt Service - Tax + Profit Distribution
            const debtService = (loanPayment?.principal || 0) + (loanPayment?.interest || 0);
            const capitalCashFlow = projectCashFlow - debtService - pnl.incomeTax + (pnl.profitDistribution || 0);
            
            data.push({
                year: pnl.year,
                projectCF: projectCashFlow,
                capitalCF: capitalCashFlow,
                explanation: `Year ${pnl.year}: Operating income ${formatCurrency(pnl.operatingIncome)}, Depreciation ${formatCurrency(depreciation)}`
            });
        });

        return data;
    }, [outputs.pnlStatement, outputs.balanceSheet, outputs.investmentPlan, outputs.loanRepaymentSchedule, inputs.ownFunds]);

    const cashFlowChart = (
        <div className="space-y-4">
            <ResponsiveContainer width="100%" height={400}>
                <LineChart data={cashFlowData} margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4A5568' : '#E2E8F0'} />
                    <XAxis 
                        dataKey="year" 
                        tick={{ fill: isDarkMode ? '#A0AEC0' : '#4A5568' }}
                        label={{ value: 'Year', position: 'insideBottom', offset: -5, fill: isDarkMode ? '#A0AEC0' : '#4A5568' }}
                    />
                    <YAxis 
                        tickFormatter={(value) => {
                            if (Math.abs(value) >= 1000000) {
                                return `$${(value / 1000000).toFixed(1)}M`;
                            } else if (Math.abs(value) >= 1000) {
                                return `$${(value / 1000).toFixed(0)}K`;
                            } else {
                                return `$${value.toFixed(0)}`;
                            }
                        }}
                        tick={{ fill: isDarkMode ? '#A0AEC0' : '#4A5568' }}
                        label={{ value: 'Cash Flow ($)', angle: -90, position: 'insideLeft', fill: isDarkMode ? '#A0AEC0' : '#4A5568' }}
                        width={80}
                    />
                <Tooltip 
                    contentStyle={{
                            backgroundColor: isDarkMode ? '#2D3748' : '#ffffff',
                            border: isDarkMode ? '1px solid #4A5568' : '1px solid #E2E8F0',
                            color: isDarkMode ? '#F3F4F6' : '#111827',
                            borderRadius: '6px'
                        }}
                        formatter={(value, name) => [formatCurrency(value, 0), name]}
                        labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend wrapperStyle={{ color: isDarkMode ? '#A0AEC0' : '#4A5568' }} />
                    <Line 
                        type="monotone" 
                        dataKey="projectCF" 
                        stroke="#3B82F6" 
                        strokeWidth={3} 
                        name="Project Cash Flow (Unlevered)" 
                        dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                    />
                    <Line 
                        type="monotone" 
                        dataKey="capitalCF" 
                        stroke="#10B981" 
                        strokeWidth={3} 
                        name="Capital Cash Flow (Equity)" 
                        dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                    />
            </LineChart>
        </ResponsiveContainer>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">💡 Understanding the Chart</h4>
                <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                    <p><strong>🔵 Project Cash Flow (Blue Line):</strong> Shows the cash generated by the project itself, before considering how it's financed. This represents the project's ability to generate cash from operations.</p>
                    <p><strong>🟢 Capital Cash Flow (Green Line):</strong> Shows the actual cash returns to equity investors after debt service. This is what matters most to equity investors.</p>
                    <p><strong>📊 Key Insights:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                        <li>Year 0: Large negative values represent initial investments</li>
                        <li>Operating Years: Positive values show cash generation</li>
                        <li>Capital CF is typically lower due to debt service payments</li>
                        <li>Both lines trend upward as debt is paid down over time</li>
                        {inputs.bessEnabled && (
                            <li>BESS projects may show periodic jumps when batteries are replaced</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );

    const menuItems = [
        { id: 'overview', label: '🌟 Overview', icon: '📊' },
        { id: 'architecture', label: '🏗️ Model Architecture', icon: '🔧' },
        { id: 'inputs', label: '🎯 Input Parameters', icon: '⚙️' },
        { id: 'calculations', label: '🧮 Financial Formulas', icon: '📐' },
        { id: 'tab-analysis', label: '📈 Tab-by-Tab Analysis', icon: '📋' },
        { id: 'relationships', label: '🔗 Input-Output Matrix', icon: '🔄' },
        { id: 'assumptions', label: '🏗️ Assumptions & Limitations', icon: '⚠️' },
        { id: 'kpis', label: '🎯 Key Performance Indicators', icon: '📊' },
        { id: 'sensitivity', label: '🔄 Sensitivity Analysis', icon: '📉' },
        { id: 'bess', label: '🔋 BESS Integration', icon: '⚡' },
        { id: 'implementation', label: '🛠️ Technical Implementation', icon: '⚙️' },
        { id: 'usage', label: '📚 Usage Guidelines', icon: '👥' },
        { id: 'extensions', label: '🔮 Model Extensions', icon: '🚀' },
        { id: 'theory', label: '🔬 Financial Theory', icon: '🧪' },
        { id: 'about', label: '👨‍💼 About', icon: 'ℹ️' }
    ];

    const renderSection = () => {
        switch(activeSection) {
            case 'overview':
                return (
                    <div className="space-y-8">
                        <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white p-8 rounded-xl">
                            <h2 className="text-3xl font-bold mb-4">🌟 Solar + BESS Financial Model</h2>
                            <p className="text-xl mb-4">Complete Reference Guide & Documentation</p>
                            <p className="text-lg opacity-90">
                                This comprehensive solar + battery energy storage system (BESS) financial model provides detailed financial analysis for utility-scale 
                                solar photovoltaic (PV) projects with optional energy storage integration. The model evaluates project feasibility from both project-level 
                                and investor-level perspectives, incorporating industry-standard financial metrics, advanced BESS revenue modeling, and storage optimization strategies.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Model Features</h3>
                                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <li>✅ 10 Interactive Analysis Tabs</li>
                                    <li>✅ Real-time Financial Calculations</li>
                                    <li>✅ BESS Integration & Revenue Modeling</li>
                                    <li>✅ Goal Seek Optimization</li>
                                    <li>✅ Export/Import Parameters</li>
                                    <li>✅ Dark Mode Interface</li>
                                    <li>✅ Comprehensive Documentation</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Key Metrics</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Project IRR:</span>
                                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.projectIRR)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Project NPV:</span>
                                        <span className="font-semibold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.projectNPV)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">LCOE:</span>
                                        <span className="font-semibold text-orange-600 dark:text-orange-400">
                                            {formatCurrency(outputs.indicators?.lcoe, 4)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Cash Flow Visualization</h3>
                            <div className="text-gray-600 dark:text-gray-300 mb-6 space-y-2">
                                <p>This chart shows the actual cash flows from your financial model, illustrating two critical perspectives:</p>
                                <div className="grid md:grid-cols-2 gap-4 mt-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">🔵 Project Cash Flow</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300">Unlevered view - how much cash the project generates regardless of financing</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">🟢 Capital Cash Flow</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300">Equity investor view - actual cash returns after debt service</p>
                                    </div>
                                </div>
                            </div>
                            {cashFlowChart}
                        </div>
                    </div>
                );
                
            case 'architecture':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🏗️ Model Architecture</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 10 Interactive Tabs</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[
                                    { name: 'Basic Data', desc: 'Input parameters and project configuration', icon: '⚙️' },
                                    { name: 'Summary', desc: 'High-level financial metrics and KPIs', icon: '📋' },
                                    { name: 'Investment & Financing', desc: 'Capital requirements and funding structure', icon: '💰' },
                                    { name: 'BESS Analysis', desc: 'Battery storage system analysis and revenue modeling', icon: '🔋' },
                                    { name: 'Loan Repayment', desc: 'Debt service schedule and cash flows', icon: '📅' },
                                    { name: 'Total Cost & Cost/kWh', desc: 'Operating cost analysis and energy costs', icon: '💵' },
                                    { name: 'Profit & Loss', desc: 'Annual income statements', icon: '📈' },
                                    { name: 'Balance Sheet', desc: 'Financial position statements', icon: '⚖️' },
                                    { name: 'EVA', desc: 'Economic Value Added analysis', icon: '💎' },
                                    { name: 'Charts', desc: 'Visual representations of key metrics', icon: '📊' }
                                ].map((tab, index) => (
                                    <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className="text-2xl">{tab.icon}</span>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">{tab.name}</h4>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">{tab.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔄 Calculation Flow</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Input Parameters</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">User defines project configuration, costs, and financial assumptions</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Investment Calculation</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Determines total investment, financing structure, and depreciation base</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Annual Operations</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Calculates yearly revenue, costs, taxes, and cash flows for each year</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">4</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">Financial Analysis</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">Computes NPV, IRR, LCOE, payback periods, and other key metrics</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'inputs':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🎯 Input Parameters & Assumptions</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Complete Parameter Reference</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Parameter</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Current Value</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Unit</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Description</th>
                                            <th className="text-left py-3 px-4 font-bold text-gray-800 dark:text-gray-100">Financial Impact</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                        <tr className="bg-blue-50 dark:bg-blue-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-blue-800 dark:text-blue-200">Project Configuration</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">constructionPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.constructionPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Time required for project construction</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Affects interest during construction</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operationPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.operationPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Project operational lifetime</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines all cash flow periods</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">installedCapacity</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.installedCapacity}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total solar installation capacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Primary driver of investment costs and revenue</td>
                                        </tr>
                                        
                                        <tr className="bg-green-50 dark:bg-green-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-green-800 dark:text-green-200">Investment Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">investmentPerMW</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.investmentPerMW)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Capital cost per megawatt installed</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Direct multiplier for total investment</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">ownFunds</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.ownFunds)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Equity contribution from investors</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines debt/equity ratio</td>
                                        </tr>
                                        
                                        <tr className="bg-orange-50 dark:bg-orange-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-orange-800 dark:text-orange-200">Revenue Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">annualHours</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.annualHours}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">hours</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Equivalent full-load hours per year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Primary revenue driver</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">onGridTariff</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.onGridTariff, 4)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/kWh</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Electricity selling price</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue calculation base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">pvDegradationRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.pvDegradationRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual efficiency decline of PV panels</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces energy output over time</td>
                                        </tr>
                                        
                                        <tr className="bg-red-50 dark:bg-red-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-red-800 dark:text-red-200">Cost Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operatingCostRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.operatingCostRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of revenue</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">O&M costs as percentage of revenue</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual operating expense</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">operatingCostEscalationRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.operatingCostEscalationRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual increase in operating costs</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cost inflation over project life</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">landCostPerAcre</td>
                                            <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.landCostPerAcre)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/acre/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual land lease cost</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Ongoing operating expense</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">acresPerMW</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.acresPerMW}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">acres/MW</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Land requirement per MW capacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines total land costs</td>
                                        </tr>
                                        
                                        <tr className="bg-purple-50 dark:bg-purple-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-purple-800 dark:text-purple-200">Financing Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">longTermLoanRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.longTermLoanRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Interest rate on project debt</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Debt service calculations</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">discountRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.discountRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Weighted Average Cost of Capital (WACC)</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">NPV and LCOE calculations</td>
                                        </tr>
                                        
                                        <tr className="bg-yellow-50 dark:bg-yellow-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-yellow-800 dark:text-yellow-200">Tax & Accounting Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">vatRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.vatRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Value Added Tax rate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">surtaxRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.surtaxRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Additional tax on VAT</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Revenue taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">incomeTaxRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.incomeTaxRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Corporate income tax rate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Profit taxation</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">depreciationPeriod</td>
                                            <td className="py-3 px-4 font-semibold">{inputs.depreciationPeriod}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Asset depreciation timeline</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Tax shield and cash flow</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">salvageValueRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.salvageValueRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Residual asset value at end of life</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Terminal value calculation</td>
                                        </tr>
                                        
                                        <tr className="bg-teal-50 dark:bg-teal-900/20">
                                            <td colSpan="5" className="py-2 px-4 font-bold text-teal-800 dark:text-teal-200">Profit Distribution Parameters</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">statutoryReserveRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.statutoryReserveRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of profit</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Legally required profit reserves</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces distributable profit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">discretionaryReserveRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.discretionaryReserveRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of profit</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Management discretionary reserves</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces distributable profit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">profitDistributionRate</td>
                                            <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.profitDistributionRate)}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Percentage of available profit distributed</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Investor cash returns</td>
                                        </tr>
                                        
                                        {inputs.bessEnabled && (
                                            <>
                                                <tr className="bg-violet-50 dark:bg-violet-900/20">
                                                    <td colSpan="5" className="py-2 px-4 font-bold text-violet-800 dark:text-violet-200">🔋 BESS Configuration Parameters</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessEnergyCapacity</td>
                                                    <td className="py-3 px-4 font-semibold">{inputs.bessEnergyCapacity}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">MWh</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Battery energy storage capacity</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Determines storage investment and revenue potential</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessPowerCapacity</td>
                                                    <td className="py-3 px-4 font-semibold">{inputs.bessPowerCapacity}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">MW</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Battery power output capacity</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Affects power-related costs and revenues</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessCostPerMWh</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessCostPerMWh)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MWh</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Battery energy storage cost per MWh</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Major component of BESS capital investment</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessPowerCostPerMW</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessPowerCostPerMW)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Power electronics cost per MW</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Inverter and BMS investment component</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessRoundTripEfficiency</td>
                                                    <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.bessRoundTripEfficiency)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Energy efficiency for charge-discharge cycle</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Directly affects energy arbitrage revenues</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessDegradationRate</td>
                                                    <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.bessDegradationRate)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">%/year</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual battery capacity degradation</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reduces energy-based revenues over time</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessReplacementPeriod</td>
                                                    <td className="py-3 px-4 font-semibold">{inputs.bessReplacementPeriod}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">years</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Battery replacement cycle</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Triggers major capital expenditures</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessReplacementCostFactor</td>
                                                    <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.bessReplacementCostFactor)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of original</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cost reduction factor for replacement batteries</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Reflects technology learning curve benefits</td>
                                                </tr>
                                                
                                                <tr className="bg-emerald-50 dark:bg-emerald-900/20">
                                                    <td colSpan="5" className="py-2 px-4 font-bold text-emerald-800 dark:text-emerald-200">💰 BESS Revenue Stream Parameters</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessArbitrageCycles</td>
                                                    <td className="py-3 px-4 font-semibold">{inputs.bessArbitrageCycles}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">cycles/day</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Daily charge-discharge cycles for arbitrage</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Multiplies daily arbitrage opportunities</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessArbitrageSpread</td>
                                                    <td className="py-3 px-4 font-semibold">{formatPercentage(inputs.bessArbitrageSpread)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">% of tariff</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Price differential for energy arbitrage</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Primary energy arbitrage revenue driver</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessAncillaryRate</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessAncillaryRate)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW/year</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Grid stability service compensation</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Power-capacity based revenue stream</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessCapacityRate</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessCapacityRate)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW/month</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Capacity market payments</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Fixed monthly revenue per MW</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessDemandChargeReduction</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessDemandChargeReduction)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW/month</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Peak demand reduction value</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Customer demand charge savings</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-mono text-blue-600 dark:text-blue-400">bessOperatingCostRate</td>
                                                    <td className="py-3 px-4 font-semibold">{formatCurrency(inputs.bessOperatingCostRate)}</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">USD/MW/year</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Annual BESS maintenance costs</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Fixed operating expense for battery system</td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 'calculations':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🧮 Financial Calculation Engine</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">1. Investment & Financing Calculations</h3>
                            <div className="space-y-6">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">Total Investment Estimation</h4>
                                    <div className="font-mono text-sm space-y-2">
                                        <div>Total Investment Estimate = Installed Capacity × Investment per MW</div>
                                        <div>= {inputs.installedCapacity} MW × {formatCurrency(inputs.investmentPerMW)} = <span className="font-bold text-green-600">{formatCurrency(inputs.installedCapacity * inputs.investmentPerMW)}</span></div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">Investment Component Breakdown</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Equipment Purchase = Total Investment × 70% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.7)}</div>
                                        <div>Installation Project = Total Investment × 15% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.15)}</div>
                                        <div>Other Construction Costs = Total Investment × 10% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.1)}</div>
                                        <div>Preparatory Work = Total Investment × 5% = {formatCurrency((inputs.installedCapacity * inputs.investmentPerMW) * 0.05)}</div>
                                        <div className="font-bold">Total Static Investment = Sum of all components above</div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-3">Construction Financing</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Capital Fund Ratio = Own Funds ÷ Total Investment Estimate</div>
                                        <div>Loan Ratio = 1 - Capital Fund Ratio</div>
                                        <div>Construction Loan = Total Investment × Loan Ratio</div>
                                        <div>Interest During Construction = Construction Loan × Long Term Loan Rate × Construction Period</div>
                                    </div>
                                </div>
                                
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-3">Working Capital & Final Investment</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Working Capital = Total Investment × 2%</div>
                                        <div>Total Investment = Total Static Investment + Interest During Construction + Working Capital</div>
                                        <div className="font-bold">Final Financing Structure:</div>
                                        <div>Capital (Equity) = Own Funds = {formatCurrency(inputs.ownFunds)}</div>
                                        <div>Loan (Debt) = Total Investment - Capital</div>
                                        <div>Total Financing = Capital + Loan</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">2. Annual Operations Calculations</h3>
                            <div className="space-y-6">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">Energy Generation with Degradation</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Degradation Factor(year) = (1 - PV Degradation Rate)^(year-1)</div>
                                        <div>Annual Generation(year) = Installed Capacity × 1000 × Annual Hours × Degradation Factor</div>
                                        <div className="mt-2 space-y-1">
                                            <div>Year 1: {inputs.installedCapacity} × 1000 × {inputs.annualHours} = {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours, 0)} kWh</div>
                                            <div>Year 10: {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours * Math.pow(1 - inputs.pvDegradationRate, 9), 0)} kWh</div>
                                            <div>Year 25: {formatCurrency(inputs.installedCapacity * 1000 * inputs.annualHours * Math.pow(1 - inputs.pvDegradationRate, 24), 0)} kWh</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">Revenue & Cost Structure</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>Operating Income(year) = Annual Generation(year) × On Grid Tariff</div>
                                        <div>Operating Cost(year=1) = Operating Income × Operating Cost Rate</div>
                                        <div>Operating Cost(year&gt;1) = Previous Year Cost × (1 + Escalation Rate)</div>
                                        <div>Land Cost(year) = Installed Capacity × Acres per MW × Land Cost per Acre</div>
                                        <div>= {inputs.installedCapacity} × {inputs.acresPerMW} × {formatCurrency(inputs.landCostPerAcre)} = {formatCurrency(inputs.installedCapacity * inputs.acresPerMW * inputs.landCostPerAcre)}/year</div>
                                    </div>
                                </div>
                                
                                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-3">Taxation & Profit Distribution</h4>
                                    <div className="font-mono text-sm space-y-1">
                                        <div>VAT Surtax = Operating Income × VAT Rate × Surtax Rate</div>
                                        <div>Total Profit = Operating Income - Total Cost - VAT Surtax</div>
                                        <div>Income Tax = MAX(0, Total Profit × Income Tax Rate)</div>
                                        <div>Net Profit = Total Profit - Income Tax</div>
                                        <div>Statutory Reserve = MAX(0, Net Profit × Statutory Reserve Rate)</div>
                                        <div>Discretionary Reserve = MAX(0, Net Profit × Discretionary Reserve Rate)</div>
                                        <div>Profit Distribution = Available Profit × Distribution Rate</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">3. Financial Indicators</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Net Present Value (NPV)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>Project NPV = Σ[Project Cash Flow(year) ÷ (1 + Discount Rate)^year]</div>
                                        <div>Capital NPV = Σ[Capital Cash Flow(year) ÷ (1 + Discount Rate)^year]</div>
                                        <div className="mt-2 font-bold">Current Project NPV: {formatCurrency(outputs.indicators?.projectNPV)}</div>
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Internal Rate of Return (IRR)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>NPV(IRR) = 0 = Σ[Cash Flow(year) ÷ (1 + IRR)^year]</div>
                                        <div>Solved iteratively using Newton-Raphson method</div>
                                        <div className="mt-2 font-bold">Current Project IRR: {formatPercentage(outputs.indicators?.projectIRR)}</div>
                                    </div>
                                </div>
                                
                                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2">LCOE Calculation</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>Total Discounted Costs = Investment + Discounted O&M - Salvage Value</div>
                                        <div>LCOE = Total Discounted Costs ÷ Discounted Energy Production</div>
                                        <div className="mt-2 font-bold">Current LCOE: {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</div>
                                    </div>
                                </div>
                                
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Economic Value Added (EVA)</h4>
                                    <div className="font-mono text-xs space-y-1">
                                        <div>NOPAT = Net Profit + Interest × (1 - Tax Rate)</div>
                                        <div>Capital Employed = Total Assets - Current Liabilities</div>
                                        <div>EVA = NOPAT - Capital Employed × Discount Rate</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'tab-analysis':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📈 Tab-by-Tab Analysis</h2>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Tab 1: Basic Data</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Input configuration and parameter adjustment</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Features:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Editable input parameters</li>
                                        <li>Goal seek functionality for target IRR</li>
                                        <li>Parameter export/import capabilities</li>
                                        <li>Real-time calculation updates</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Goal Seek Function:</strong> Automatically adjusts the onGridTariff parameter to achieve a target project IRR using iterative optimization.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Tab 2: Summary</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Executive overview of project financials</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Metrics Displayed:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Project IRR, NPV, and payback periods</li>
                                        <li>Capital IRR and NPV (investor perspective)</li>
                                        <li>LCOE and average cost per kWh</li>
                                        <li>Total project investment and financing summary</li>
                                        <li>EVA totals</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Critical Insight:</strong> Distinguishes between project-level returns (independent of financing) and capital returns (equity investor perspective).</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Tab 3: Investment & Financing</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Capital requirements and funding structure analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Components:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Total investment breakdown by category</li>
                                        <li>Financing structure (debt vs. equity)</li>
                                        <li>Construction loan analysis</li>
                                        <li>Working capital requirements</li>
                                        <li>BESS investment breakdown when enabled</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Relationships:</strong> Shows how investment per MW scales with capacity and how equity percentage affects loan requirements.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔋 Tab 4: BESS Analysis</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Comprehensive battery energy storage system analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Features:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>BESS configuration summary and investment breakdown</li>
                                        <li>Multiple revenue stream analysis (arbitrage, ancillary, capacity, demand charge reduction)</li>
                                        <li>Performance tracking with degradation modeling</li>
                                        <li>Battery replacement cycle planning and costs</li>
                                        <li>Financial impact assessment on overall project returns</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Strategic Value:</strong> Demonstrates how energy storage enhances project economics through revenue diversification and grid services monetization.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📅 Tab 5: Loan Repayment</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Debt service schedule and cash flow analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Features:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Annual principal and interest payments</li>
                                        <li>Remaining loan balance tracking</li>
                                        <li>Total debt service over project life</li>
                                        <li>Cumulative payments analysis</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Cash Flow Impact:</strong> Loan payments directly reduce available cash for distribution to equity investors.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💵 Tab 6: Total Cost & Cost/kWh</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Operating cost analysis and unit energy costs</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Cost Categories:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Operating & maintenance costs</li>
                                        <li>Land lease costs</li>
                                        <li>Depreciation (non-cash)</li>
                                        <li>Interest on debt</li>
                                        <li>Total cost per kWh analysis</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Key Insight:</strong> Separates cash and non-cash costs, showing the evolution of cost structure over project life.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Tab 7: Profit & Loss</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Annual income statement analysis</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>P&L Components:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Operating income from energy sales{inputs.bessEnabled ? ' and BESS revenues' : ''}</li>
                                        <li>VAT and surtax calculations</li>
                                        <li>Operating expenses{inputs.bessEnabled ? ' including BESS O&M' : ''}</li>
                                        <li>Depreciation and interest{inputs.bessEnabled ? ' (multi-asset BESS tracking)' : ''}</li>
                                        <li>Income tax calculations</li>
                                        <li>Profit distribution and reserves</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Reserve Management:</strong> Models statutory and discretionary reserves as per corporate governance requirements.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚖️ Tab 8: Balance Sheet</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Financial position and asset management</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Balance Sheet Items:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Fixed assets (net of depreciation){inputs.bessEnabled ? ' including multi-asset BESS tracking' : ''}</li>
                                        <li>Current assets (primarily cash)</li>
                                        <li>Long-term debt</li>
                                        <li>Equity and accumulated profits</li>
                                        <li>Balance verification</li>
                                        {inputs.bessEnabled && <li>BESS replacement cost tracking and asset lifecycle management</li>}
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Financial Health:</strong> Tracks asset base erosion through depreciation and cash generation from operations{inputs.bessEnabled ? ', with special handling for staggered BESS asset replacements' : ''}.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💎 Tab 9: EVA</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Value creation analysis beyond accounting profits</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>EVA Analysis:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Net Operating Profit After Tax (NOPAT)</li>
                                        <li>Capital employed calculations</li>
                                        <li>Cost of capital application</li>
                                        <li>Annual value creation/destruction</li>
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Strategic Insight:</strong> EVA measures whether the project creates value above the cost of capital, providing a superior metric to accounting profit alone.</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Tab 10: Charts</h3>
                                <div className="space-y-3">
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Purpose:</strong> Visual analysis and trend identification</p>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Chart Types:</strong></p>
                                    <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>Cash flow trends over time{inputs.bessEnabled ? ' with BESS replacement cycles' : ''}</li>
                                        <li>Cost breakdown analysis{inputs.bessEnabled ? ' including BESS costs' : ''}</li>
                                        <li>Profitability trends{inputs.bessEnabled ? ' showing BESS revenue impact' : ''}</li>
                                        <li>Financial ratio evolution</li>
                                        {inputs.bessEnabled && <li>BESS-specific revenue pattern analysis and degradation impact visualization</li>}
                                    </ul>
                                    <p className="text-gray-600 dark:text-gray-300"><strong>Analytical Value:</strong> Visual representation helps identify inflection points, trends, and potential issues in project performance{inputs.bessEnabled ? ', including BESS replacement cycles and revenue recovery patterns' : ''}.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'relationships':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔗 Input-Output Relationship Matrix</h2>
                        
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Primary Impact Relationships</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-600">
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Input Parameter</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Direct Impact</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">Secondary Effects</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">installedCapacity</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total Investment, Annual Generation</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">All revenue metrics, financing requirements</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">onGridTariff</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Operating Income</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total Profit, Income Tax, all return metrics</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">longTermLoanRate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Interest Payments</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cash flows, profitability, all return metrics</td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">discountRate</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">NPV calculations, EVA</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Project viability assessment</td>
                                        </tr>
                                        {inputs.bessEnabled && (
                                            <>
                                                <tr className="bg-violet-50 dark:bg-violet-900/20">
                                                    <td colSpan="3" className="py-2 px-4 font-bold text-violet-800 dark:text-violet-200">🔋 BESS Parameter Impacts</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">bessEnergyCapacity</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">BESS Investment, Arbitrage Revenue</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Total project investment, revenue diversification</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">bessPowerCapacity</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Power costs, Ancillary revenues</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Capacity and demand charge revenues</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">bessArbitrageSpread</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Energy arbitrage revenue</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Major revenue stream, project IRR impact</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">bessDegradationRate</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Energy-based revenue decline</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Long-term revenue projections, replacement timing</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-4 font-medium text-blue-600 dark:text-blue-400">bessReplacementPeriod</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Capital expenditure cycles</td>
                                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">Cash flow timing, depreciation schedules</td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Revenue Dependencies</h3>
                                <div className="space-y-3">
                                    {[
                                        { param: 'Installed Capacity', impact: 'Scales all generation', multiplier: 'Linear' },
                                        { param: 'Annual Hours', impact: 'Capacity factor effect', multiplier: 'Linear' },
                                        { param: 'Grid Tariff', impact: 'Revenue per kWh', multiplier: 'Linear' },
                                        { param: 'Degradation Rate', impact: 'Long-term revenue', multiplier: 'Exponential decline' },
                                        ...(inputs.bessEnabled ? [
                                            { param: 'BESS Energy Capacity', impact: 'Arbitrage volume', multiplier: 'Linear' },
                                            { param: 'Arbitrage Spread', impact: 'Energy arbitrage revenue', multiplier: 'Linear' },
                                            { param: 'Ancillary Rate', impact: 'Grid services revenue', multiplier: 'Linear' },
                                            { param: 'Capacity Rate', impact: 'Monthly capacity payments', multiplier: 'Linear' }
                                        ] : [])
                                    ].map((item, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                            <span className="font-medium text-blue-800 dark:text-blue-200">{item.param}</span>
                                            <div className="text-right">
                                                <div className="text-sm text-blue-600 dark:text-blue-400">{item.impact}</div>
                                                <div className="text-xs text-blue-500 dark:text-blue-300">{item.multiplier}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Cost Dependencies</h3>
                                <div className="space-y-3">
                                    {[
                                        { param: 'Investment per MW', impact: 'Capital costs', multiplier: 'Linear' },
                                        { param: 'Operating Cost Rate', impact: 'Annual O&M', multiplier: 'Percentage of revenue' },
                                        { param: 'Land Cost per Acre', impact: 'Ongoing land costs', multiplier: 'Linear' },
                                        { param: 'Cost Escalation', impact: 'Future cost growth', multiplier: 'Compound growth' },
                                        ...(inputs.bessEnabled ? [
                                            { param: 'BESS Cost per MWh', impact: 'Battery investment', multiplier: 'Linear' },
                                            { param: 'Power Cost per MW', impact: 'Inverter costs', multiplier: 'Linear' },
                                            { param: 'BESS Operating Cost', impact: 'Annual maintenance', multiplier: 'Linear' },
                                            { param: 'Replacement Cost Factor', impact: 'Future replacements', multiplier: 'Percentage reduction' }
                                        ] : [])
                                    ].map((item, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                            <span className="font-medium text-green-800 dark:text-green-200">{item.param}</span>
                                            <div className="text-right">
                                                <div className="text-sm text-green-600 dark:text-green-400">{item.impact}</div>
                                                <div className="text-xs text-green-500 dark:text-green-300">{item.multiplier}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'assumptions':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🏗️ Model Assumptions & Limitations</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Engineering Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Linear Degradation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">PV degradation follows a constant annual rate</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Capacity Factor</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes consistent annual full-load hours</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Land Requirements</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Fixed 5 acres per MW ratio</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Technology</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Standard crystalline silicon PV technology</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Grid Connection</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes stable grid access and no curtailment</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Financial Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Constant Tariff</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No escalation in electricity selling price</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Inflation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Only operating costs inflate; revenues remain constant</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Tax Stability</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Tax rates remain constant throughout project life</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Perfect Markets</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No transaction costs or market frictions</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Debt Service</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Straight-line principal repayment (not annuity)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Economic Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Discount Rate</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Remains constant (no term structure)</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Currency</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">All calculations in nominal USD terms</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Political Risk</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Assumes stable regulatory environment</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Environmental</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No carbon pricing or environmental costs/benefits</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Maintenance</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">O&M costs scale with revenue, not actual maintenance needs</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📋 Accounting Assumptions</h3>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Depreciation</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Straight-line method over 25 years</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Working Capital</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Fixed 2% of total investment</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Construction</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Single-year construction period</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Reserves</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Statutory and discretionary reserves as per corporate law</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Cash Management</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">No specific cash management strategies modeled</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-yellow-800 dark:text-yellow-200 mb-4">⚠️ Important Limitations</h3>
                            <p className="text-yellow-700 dark:text-yellow-300 mb-4">
                                These assumptions simplify the complex reality of solar project development and operation. 
                                Users should consider these limitations when interpreting results and making investment decisions.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Model Simplifications:</h4>
                                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                        <li>• No weather variability modeling</li>
                                        <li>• Simplified O&M cost structure</li>
                                        <li>• No equipment failure scenarios</li>
                                        <li>• Single financing structure</li>
                                    </ul>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Real-World Considerations:</h4>
                                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                                        <li>• Regulatory changes</li>
                                        <li>• Technology improvements</li>
                                        <li>• Market price volatility</li>
                                        <li>• Force majeure events</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'kpis':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🎯 Key Performance Indicators (KPIs)</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Investment Viability Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Project IRR &gt; Discount Rate</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Indicates project viability</p>
                                        <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.projectIRR)} vs {formatPercentage(inputs.discountRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? '✅ VIABLE' : '❌ NOT VIABLE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Project NPV</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Confirms value creation</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.projectNPV)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.projectNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.projectNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">LCOE &lt; Market Price</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Ensures competitive positioning</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatCurrency(outputs.indicators?.lcoe, 4)}/kWh
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Payback Period &lt; 10 years</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Acceptable risk profile</p>
                                        <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                            {formatYears(outputs.indicators?.paybackPeriod)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.paybackPeriod || 0) < 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.paybackPeriod || 0) < 10 ? '✅ ACCEPTABLE' : '❌ TOO LONG'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Investor Return Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Capital IRR &gt; Target Return</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate equity returns</p>
                                        <div className="mt-2 text-lg font-bold text-blue-600 dark:text-blue-400">
                                            {formatPercentage(outputs.indicators?.capitalIRR)}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Positive Capital NPV</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Equity value creation</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatCurrency(outputs.indicators?.capitalNPV)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.indicators?.capitalNPV || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.indicators?.capitalNPV || 0) > 0 ? '✅ POSITIVE' : '❌ NEGATIVE'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">Stable Cash Distributions</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Predictable investor returns</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatPercentage(inputs.profitDistributionRate)} of profit
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Positive EVA</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Value creation above cost of capital</p>
                                        <div className="mt-2 text-lg font-bold text-purple-600 dark:text-purple-400">
                                            {formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0) > 0 ? '✅ CREATING VALUE' : '❌ DESTROYING VALUE'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🏥 Financial Health Metrics</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-blue-800 dark:text-blue-200">Debt Service Coverage &gt; 1.2</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">Adequate cash flow for debt service</p>
                                        <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                                            Calculated from annual cash flows
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-green-800 dark:text-green-200">Operating Margin &gt; 20%</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">Healthy operational efficiency</p>
                                        <div className="mt-2 text-lg font-bold text-green-600 dark:text-green-400">
                                            {formatPercentage(1 - inputs.operatingCostRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${(1 - inputs.operatingCostRate) > 0.2 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {(1 - inputs.operatingCostRate) > 0.2 ? '✅ HEALTHY' : '❌ LOW MARGIN'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-orange-800 dark:text-orange-200">Cost Escalation &lt; 3%</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">Controlled cost growth</p>
                                        <div className="mt-2 text-lg font-bold text-orange-600 dark:text-orange-400">
                                            {formatPercentage(inputs.operatingCostEscalationRate)}
                                        </div>
                                        <div className={`text-sm mt-1 ${inputs.operatingCostEscalationRate < 0.03 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {inputs.operatingCostEscalationRate < 0.03 ? '✅ CONTROLLED' : '❌ HIGH ESCALATION'}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-semibold text-purple-800 dark:text-purple-200">Balance Sheet Balance = 0</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">Accounting accuracy verification</p>
                                        <div className="mt-2 text-sm text-purple-600 dark:text-purple-400">
                                            Verified in Balance Sheet tab
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'sensitivity':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔄 Sensitivity Analysis & Investment Decision Framework</h2>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
                            <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200 mb-4">💡 Purpose & Strategic Value</h3>
                            <p className="text-blue-700 dark:text-blue-300 mb-4">
                                Sensitivity analysis is the cornerstone of prudent solar investment decision-making. It reveals how changes in key assumptions impact project returns, 
                                helping investors understand risk exposure and identify the most critical success factors.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">🎯 Risk Assessment</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Quantify downside scenarios and probability of losses</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">📊 Scenario Planning</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">Model best-case, worst-case, and most-likely outcomes</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">🔧 Optimization</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Identify which parameters to focus on for maximum impact</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Critical Success Factors (Ranked by Impact)</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-red-500 pl-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-red-800 dark:text-red-200">1. Electricity Tariff (On-Grid Price)</h4>
                                        <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                            <strong>Impact:</strong> Direct linear effect on all revenue streams
                                        </p>
                                        <p className="text-xs text-red-600 dark:text-red-400">
                                            <strong>Current Value:</strong> ${formatCurrency(inputs.onGridTariff, 4)}/kWh | 
                                            <strong> Risk:</strong> Regulatory changes, market competition | 
                                            <strong> Mitigation:</strong> Long-term power purchase agreements (PPAs)
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">2. Capital Investment Cost ($/MW)</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on initial investment, debt requirements, and depreciation
                                        </p>
                                        <p className="text-xs text-orange-600 dark:text-orange-400">
                                            <strong>Current Value:</strong> ${formatCurrency(inputs.investmentPerMW, 0)}/MW | 
                                            <strong> Risk:</strong> Technology costs, supply chain, labor | 
                                            <strong> Mitigation:</strong> Fixed-price EPC contracts, hedging strategies
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-yellow-500 pl-4 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-yellow-800 dark:text-yellow-200">3. Financing Cost (Interest Rate)</h4>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                                            <strong>Impact:</strong> Exponential effect on debt service and equity returns
                                        </p>
                                        <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                            <strong>Current Value:</strong> {formatPercentage(inputs.longTermLoanRate)} | 
                                            <strong> Risk:</strong> Interest rate cycles, credit rating changes | 
                                            <strong> Mitigation:</strong> Fixed-rate financing, interest rate swaps
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">4. Capacity Factor (Annual Hours)</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on energy generation and revenue
                                        </p>
                                        <p className="text-xs text-green-600 dark:text-green-400">
                                            <strong>Current Value:</strong> {inputs.annualHours.toLocaleString()} hours | 
                                            <strong> Risk:</strong> Weather patterns, equipment performance | 
                                            <strong> Mitigation:</strong> Conservative estimates, performance monitoring
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">5. Operating Costs (% of Revenue)</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                                            <strong>Impact:</strong> Direct effect on margins and cash flows
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400">
                                            <strong>Current Value:</strong> {formatPercentage(inputs.operatingCostRate)} | 
                                            <strong> Risk:</strong> Inflation, maintenance complexity | 
                                            <strong> Mitigation:</strong> Service contracts, predictive maintenance
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Parameter Interactions & Compound Effects</h3>
                                <div className="space-y-4">
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">🔄 Revenue Amplification Chain</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-2">
                                            <strong>Capacity ↗️ × Hours ↗️ × Tariff ↗️ = Revenue³</strong> (cubic growth effect)
                                        </p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                            Example: 10% increase in each = {formatPercentage(1.1*1.1*1.1-1)} total revenue increase
                                        </p>
                                    </div>
                                    
                                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-red-800 dark:text-red-200">⚠️ Leverage Effect on Returns</h4>
                                        <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                                            <strong>Interest Rate ↗️ + Investment Cost ↗️ = Equity IRR ↘️↘️</strong> (amplified negative impact)
                                        </p>
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                            Higher leverage means small revenue/cost changes create large equity return swings
                                        </p>
                                    </div>
                                    
                                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-amber-800 dark:text-amber-200">📉 Degradation Compounding</h4>
                                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-2">
                                            <strong>PV Degradation × Time = Exponential Revenue Loss</strong>
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            Current {formatPercentage(inputs.pvDegradationRate)}/year = {formatPercentage(Math.pow(1-inputs.pvDegradationRate, 25)-1)} total loss over 25 years
                                        </p>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">💰 Tax Shield Optimization</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            <strong>Depreciation + Interest = Tax Savings</strong>
                                        </p>
                                        <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                                            Higher debt ratio increases tax benefits but also increases financial risk
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 Investment Decision Framework</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">✅ GO/NO-GO Criteria</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Base Case Requirements</h5>
                                            <ul className="text-sm text-green-600 dark:text-green-400 mt-1 space-y-1">
                                                <li>• Project IRR &gt; {formatPercentage(inputs.discountRate + 0.03)} (300bp premium)</li>
                                                <li>• Capital IRR &gt; {formatPercentage(inputs.discountRate + 0.05)} (500bp premium)</li>
                                                <li>• Payback &lt; 8 years</li>
                                                <li>• Positive NPV in all scenarios</li>
                                            </ul>
                                        </div>
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Stress Test Survival</h5>
                                            <ul className="text-sm text-yellow-600 dark:text-yellow-400 mt-1 space-y-1">
                                                <li>• -20% tariff scenario: IRR &gt; discount rate</li>
                                                <li>• +30% capex scenario: Still profitable</li>
                                                <li>• +200bp financing rate: Acceptable returns</li>
                                                <li>• -15% generation: Positive cash flows</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">📊 Risk Assessment Matrix</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">High Impact / High Probability</h5>
                                            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-1 space-y-1">
                                                <li>• Electricity price volatility</li>
                                                <li>• Equipment cost inflation</li>
                                                <li>• Regulatory changes</li>
                                            </ul>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">High Impact / Low Probability</h5>
                                            <ul className="text-sm text-orange-600 dark:text-orange-400 mt-1 space-y-1">
                                                <li>• Major policy shifts</li>
                                                <li>• Technology disruption</li>
                                                <li>• Force majeure events</li>
                                            </ul>
                                        </div>
                                        <div className="bg-gray-50 dark:bg-gray-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-gray-700 dark:text-gray-300">Monitor Continuously</h5>
                                            <ul className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                                                <li>• Performance degradation</li>
                                                <li>• Financing market conditions</li>
                                                <li>• Grid stability & curtailment</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">🔧 Optimization Strategies</h4>
                                    <div className="space-y-3">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Revenue Enhancement</h5>
                                            <ul className="text-sm text-purple-600 dark:text-purple-400 mt-1 space-y-1">
                                                <li>• Negotiate long-term PPAs</li>
                                                <li>• Optimize plant location</li>
                                                <li>• Premium equipment selection</li>
                                            </ul>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Cost Optimization</h5>
                                            <ul className="text-sm text-indigo-600 dark:text-indigo-400 mt-1 space-y-1">
                                                <li>• Competitive EPC bidding</li>
                                                <li>• Efficient financing structure</li>
                                                <li>• Performance-based O&M</li>
                                            </ul>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Risk Mitigation</h5>
                                            <ul className="text-sm text-pink-600 dark:text-pink-400 mt-1 space-y-1">
                                                <li>• Insurance coverage</li>
                                                <li>• Performance guarantees</li>
                                                <li>• Hedging strategies</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-blue-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Recommended Analysis Workflow</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">1️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Base Case</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Establish realistic assumptions. Current model shows:
                                        </p>
                                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                            Project IRR: {formatPercentage(outputs.indicators?.projectIRR)}<br/>
                                            Capital IRR: {formatPercentage(outputs.indicators?.capitalIRR)}<br/>
                                            NPV: {formatCurrency(outputs.indicators?.projectNPV, 0)}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">2️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Single Variable</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Test each critical factor ±20%
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • Tariff: ±$0.01/kWh<br/>
                                            • Capex: ±$200k/MW<br/>
                                            • Interest: ±2%<br/>
                                            • Hours: ±300/year
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">3️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Multi-Variable</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Create 3×3 scenario matrices
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • Tariff vs Capex<br/>
                                            • Interest vs Hours<br/>
                                            • Opex vs Degradation<br/>
                                            • Financing vs Equity
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div className="text-center">
                                        <span className="text-2xl">4️⃣</span>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-100 mt-2">Monte Carlo</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                            Probability distributions for key variables
                                        </p>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            • 1000+ simulations<br/>
                                            • P10/P50/P90 outcomes<br/>
                                            • Risk metrics<br/>
                                            • Correlation effects
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-600">
                                <h4 className="font-bold text-blue-800 dark:text-blue-200 mb-2">💡 Pro Tip: Focus on Controllable vs. Market Factors</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <h5 className="font-semibold text-green-700 dark:text-green-300">✅ Controllable (Optimize These)</h5>
                                        <ul className="text-green-600 dark:text-green-400 mt-1 space-y-1">
                                            <li>• Equipment selection & cost</li>
                                            <li>• Financing structure & terms</li>
                                            <li>• Site selection & design</li>
                                            <li>• O&M strategy & costs</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-orange-700 dark:text-orange-300">⚠️ Market-Driven (Hedge/Mitigate)</h5>
                                        <ul className="text-orange-600 dark:text-orange-400 mt-1 space-y-1">
                                            <li>• Electricity prices & tariffs</li>
                                            <li>• Interest rates & credit markets</li>
                                            <li>• Regulatory environment</li>
                                            <li>• Weather & resource variability</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'bess':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔋 BESS Integration - Comprehensive Analysis</h2>
                        
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-8 rounded-xl border border-green-200 dark:border-green-700">
                            <h3 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-4">🌟 Solar + BESS: The Future of Energy Storage</h3>
                            <p className="text-lg text-green-700 dark:text-green-300 mb-6">
                                Battery Energy Storage Systems (BESS) represent the next evolution in solar project optimization. By pairing solar generation 
                                with advanced battery storage, projects unlock multiple revenue streams, enhance grid stability, and provide unparalleled 
                                energy management capabilities that transform the economics of renewable energy investments.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-green-200 dark:border-green-600">
                                    <div className="text-center">
                                        <span className="text-4xl mb-3 block">⚡</span>
                                        <h4 className="text-lg font-bold text-green-800 dark:text-green-200">Energy Arbitrage</h4>
                                        <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                                            Store energy during low-price periods and sell during peak demand for maximum revenue optimization
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-blue-200 dark:border-blue-600">
                                    <div className="text-center">
                                        <span className="text-4xl mb-3 block">🏭</span>
                                        <h4 className="text-lg font-bold text-blue-800 dark:text-blue-200">Grid Services</h4>
                                        <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                                            Provide frequency regulation, voltage support, and black-start capabilities for additional revenue streams
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-purple-200 dark:border-purple-600">
                                    <div className="text-center">
                                        <span className="text-4xl mb-3 block">📊</span>
                                        <h4 className="text-lg font-bold text-purple-800 dark:text-purple-200">Demand Management</h4>
                                        <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
                                            Reduce peak demand charges and provide load balancing for commercial and industrial customers
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🏗️ BESS Implementation Overview</h3>
                                <div className="space-y-4">
                                    {inputs.bessEnabled && (
                                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-600">
                                            <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">✅ BESS Currently Enabled</h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-green-700 dark:text-green-300"><strong>Energy Capacity:</strong> {inputs.bessEnergyCapacity} MWh</p>
                                                    <p className="text-green-700 dark:text-green-300"><strong>Power Capacity:</strong> {inputs.bessPowerCapacity} MW</p>
                                                </div>
                                                <div>
                                                    <p className="text-green-700 dark:text-green-300"><strong>Round-trip Efficiency:</strong> {formatPercentage(inputs.bessRoundTripEfficiency)}</p>
                                                    <p className="text-green-700 dark:text-green-300"><strong>Annual Degradation:</strong> {formatPercentage(inputs.bessDegradationRate)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="space-y-3">
                                        <h4 className="font-semibold text-gray-800 dark:text-gray-100">System Architecture Components:</h4>
                                        <div className="space-y-2">
                                            <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                                <span className="text-blue-600 dark:text-blue-400">🔋</span>
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-gray-100">Lithium-ion Battery Modules</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">High energy density, long cycle life, rapid response capabilities</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                                <span className="text-purple-600 dark:text-purple-400">⚡</span>
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-gray-100">Power Conversion Systems</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">Bi-directional inverters for AC/DC conversion and grid synchronization</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                                <span className="text-green-600 dark:text-green-400">🧠</span>
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-gray-100">Energy Management System</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">Advanced algorithms for optimization, safety monitoring, and grid interaction</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                                <span className="text-orange-600 dark:text-orange-400">🛡️</span>
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-gray-100">Safety & Control Systems</p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">Fire suppression, thermal management, and protective relaying</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Revenue Stream Analysis</h3>
                                {inputs.bessEnabled && outputs.bessStatement && outputs.bessStatement[0] && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                                <h5 className="font-semibold text-blue-800 dark:text-blue-200 text-sm">Energy Arbitrage</h5>
                                                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{formatCurrency(outputs.bessStatement[0].arbitrageRevenue, 0)}</p>
                                                <p className="text-xs text-blue-600 dark:text-blue-400">Year 1 Revenue</p>
                                            </div>
                                            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                                <h5 className="font-semibold text-green-800 dark:text-green-200 text-sm">Ancillary Services</h5>
                                                <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(outputs.bessStatement[0].ancillaryRevenue, 0)}</p>
                                                <p className="text-xs text-green-600 dark:text-green-400">Year 1 Revenue</p>
                                            </div>
                                            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                                <h5 className="font-semibold text-purple-800 dark:text-purple-200 text-sm">Capacity Revenue</h5>
                                                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{formatCurrency(outputs.bessStatement[0].capacityRevenue, 0)}</p>
                                                <p className="text-xs text-purple-600 dark:text-purple-400">Year 1 Revenue</p>
                                            </div>
                                            <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                                <h5 className="font-semibold text-orange-800 dark:text-orange-200 text-sm">Demand Reduction</h5>
                                                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{formatCurrency(outputs.bessStatement[0].demandChargeReduction, 0)}</p>
                                                <p className="text-xs text-orange-600 dark:text-orange-400">Year 1 Revenue</p>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-gray-50 dark:bg-gray-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Financial Impact Summary</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                                <div className="text-center">
                                                    <p className="text-gray-600 dark:text-gray-300">Total BESS Investment</p>
                                                    <p className="font-bold text-red-600 dark:text-red-400">{formatCurrency(outputs.investmentPlan?.bessInvestment || 0, 0)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-gray-600 dark:text-gray-300">Annual BESS Revenue</p>
                                                    <p className="font-bold text-green-600 dark:text-green-400">{formatCurrency((outputs.bessStatement[0].arbitrageRevenue + outputs.bessStatement[0].ancillaryRevenue + outputs.bessStatement[0].capacityRevenue + outputs.bessStatement[0].demandChargeReduction), 0)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-gray-600 dark:text-gray-300">BESS Payback Period</p>
                                                    <p className="font-bold text-blue-600 dark:text-blue-400">{formatYears((outputs.investmentPlan?.bessInvestment || 0) / ((outputs.bessStatement[0].arbitrageRevenue + outputs.bessStatement[0].ancillaryRevenue + outputs.bessStatement[0].capacityRevenue + outputs.bessStatement[0].demandChargeReduction - outputs.bessStatement[0].operatingCost) || 1))}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {!inputs.bessEnabled && (
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-600">
                                        <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                                            ⚠️ BESS is currently disabled. Enable BESS in the Basic Data tab to see revenue stream analysis and financial impact.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6">🎯 BESS Benefits - Transformative Advantages</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">💰 Financial Benefits</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Revenue Diversification</h5>
                                            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                                Multiple income streams reduce dependency on energy sales alone, creating more stable and predictable cash flows
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">Energy Arbitrage Profits</h5>
                                            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                                                Capture value from electricity price volatility by storing low-cost energy and selling during peak-price periods
                                            </p>
                                        </div>
                                        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-teal-700 dark:text-teal-300">Ancillary Service Revenue</h5>
                                            <p className="text-sm text-teal-600 dark:text-teal-400 mt-1">
                                                Grid stability services (frequency regulation, voltage support) provide high-value, long-term revenue contracts
                                            </p>
                                        </div>
                                        <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-cyan-700 dark:text-cyan-300">Capacity Market Participation</h5>
                                            <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-1">
                                                Receive payments for providing dispatchable capacity to support grid reliability during peak demand periods
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">⚡ Technical Benefits</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Grid Stability Enhancement</h5>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                                Fast-responding storage provides instantaneous grid balancing, improving power quality and reducing transmission losses
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Renewable Integration</h5>
                                            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
                                                Smooths solar output variability, enabling higher renewable penetration and reducing curtailment risks
                                            </p>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Black Start Capability</h5>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                                Can restart grid operations without external power, providing critical infrastructure resilience
                                            </p>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Transmission Deferral</h5>
                                            <p className="text-sm text-pink-600 dark:text-pink-400 mt-1">
                                                Local storage can delay expensive transmission upgrades by managing local load and generation imbalances
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">🌍 Strategic Benefits</h4>
                                    <div className="space-y-3">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Market Position</h5>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                                First-mover advantage in emerging storage markets with potential for premium pricing and preferential contracts
                                            </p>
                                        </div>
                                        <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-violet-700 dark:text-violet-300">Regulatory Compliance</h5>
                                            <p className="text-sm text-violet-600 dark:text-violet-400 mt-1">
                                                Helps meet renewable energy mandates and carbon reduction targets more effectively than solar alone
                                            </p>
                                        </div>
                                        <div className="bg-fuchsia-50 dark:bg-fuchsia-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-fuchsia-700 dark:text-fuchsia-300">Customer Value Proposition</h5>
                                            <p className="text-sm text-fuchsia-600 dark:text-fuchsia-400 mt-1">
                                                Provides dispatchable clean energy, backup power, and demand charge reduction for commercial customers
                                            </p>
                                        </div>
                                        <div className="bg-rose-50 dark:bg-rose-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-rose-700 dark:text-rose-300">Technology Learning</h5>
                                            <p className="text-sm text-rose-600 dark:text-rose-400 mt-1">
                                                Operational experience with storage technology provides competitive advantage for future projects
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6">⚠️ BESS Challenges - Critical Considerations</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-red-800 dark:text-red-200">🔴 Technical Challenges</h4>
                                    <div className="space-y-3">
                                        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-600">
                                            <h5 className="font-semibold text-red-700 dark:text-red-300">Battery Degradation</h5>
                                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                <strong>Issue:</strong> Capacity loss of ~2% annually affects revenue generation over time<br/>
                                                <strong>Impact:</strong> Reduced arbitrage capacity and ancillary service availability<br/>
                                                <strong>Mitigation:</strong> Conservative degradation modeling, performance guarantees, replacement reserves
                                            </p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-600">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">Thermal Management</h5>
                                            <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                                                <strong>Issue:</strong> Heat generation during cycling reduces efficiency and lifespan<br/>
                                                <strong>Impact:</strong> Higher O&M costs, potential safety risks<br/>
                                                <strong>Mitigation:</strong> Advanced cooling systems, temperature monitoring, cycle optimization
                                            </p>
                                        </div>
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-600">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Grid Integration Complexity</h5>
                                            <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                                                <strong>Issue:</strong> Complex control systems required for bi-directional power flow<br/>
                                                <strong>Impact:</strong> Higher engineering costs, longer development timelines<br/>
                                                <strong>Mitigation:</strong> Experienced EPC contractors, proven technology platforms
                                            </p>
                                        </div>
                                        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-600">
                                            <h5 className="font-semibold text-amber-700 dark:text-amber-300">Safety & Fire Risk</h5>
                                            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                                                <strong>Issue:</strong> Thermal runaway potential in lithium-ion systems<br/>
                                                <strong>Impact:</strong> Insurance costs, regulatory compliance, public acceptance<br/>
                                                <strong>Mitigation:</strong> Advanced fire suppression, spacing requirements, certified systems
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">💰 Financial Challenges</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">High Capital Costs</h5>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                                <strong>Issue:</strong> BESS adds ~30-50% to total project investment<br/>
                                                <strong>Impact:</strong> Higher financing requirements, extended payback periods<br/>
                                                <strong>Mitigation:</strong> Declining battery costs, revenue diversification, tax incentives
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-600">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Revenue Uncertainty</h5>
                                            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
                                                <strong>Issue:</strong> Arbitrage and ancillary service prices are volatile and market-dependent<br/>
                                                <strong>Impact:</strong> Cash flow variability, financing challenges<br/>
                                                <strong>Mitigation:</strong> Conservative revenue projections, diversified revenue streams, market analysis
                                            </p>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Market Saturation Risk</h5>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                                <strong>Issue:</strong> Increasing BESS deployment may reduce arbitrage spreads<br/>
                                                <strong>Impact:</strong> Declining revenue per MWh stored and dispatched<br/>
                                                <strong>Mitigation:</strong> First-mover advantage, operational excellence, new service development
                                            </p>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg border border-pink-200 dark:border-pink-600">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Replacement Costs</h5>
                                            <p className="text-sm text-pink-600 dark:text-pink-400 mt-1">
                                                <strong>Issue:</strong> Battery replacement required every 10-15 years<br/>
                                                <strong>Impact:</strong> Significant capital expenditure during operations<br/>
                                                <strong>Mitigation:</strong> Replacement reserves, technology warranties, cost reduction projections
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6">📊 BESS Implementation Implications</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">💼 Project Finance Implications</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Enhanced Bankability</h5>
                                            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                                Multiple revenue streams and dispatchable generation improve debt capacity and reduce financing costs
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">Risk Diversification</h5>
                                            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                                                Reduced exposure to single revenue stream volatility through storage arbitrage and grid services
                                            </p>
                                        </div>
                                        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-teal-700 dark:text-teal-300">Cash Flow Optimization</h5>
                                            <p className="text-sm text-teal-600 dark:text-teal-400 mt-1">
                                                Storage enables time-shifting of energy sales to maximize revenue during peak pricing periods
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">🎯 Strategic Market Position</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Competitive Advantage</h5>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                                Dispatchable renewable energy commands premium pricing and preferential contract terms
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Market Expansion</h5>
                                            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
                                                Access to commercial/industrial markets requiring backup power and demand charge reduction
                                            </p>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Technology Leadership</h5>
                                            <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                                Early adoption positions for future grid modernization and storage mandates
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-green-50 dark:from-gray-900/50 dark:to-green-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🚀 Future Outlook & Recommendations</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-green-200 dark:border-green-600">
                                    <h4 className="font-bold text-green-800 dark:text-green-200 mb-2">✅ Key Success Factors</h4>
                                    <ul className="text-sm text-green-600 dark:text-green-400 space-y-1">
                                        <li>• Conservative revenue projections</li>
                                        <li>• Proven technology partnerships</li>
                                        <li>• Comprehensive O&M strategies</li>
                                        <li>• Market-specific optimization</li>
                                        <li>• Regulatory compliance focus</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200 mb-2">📈 Market Trends</h4>
                                    <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                                        <li>• Declining battery costs (10%+ annually)</li>
                                        <li>• Growing ancillary service markets</li>
                                        <li>• Increasing renewable mandates</li>
                                        <li>• Grid modernization investments</li>
                                        <li>• Enhanced tax incentives (ITC/PTC)</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-600">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200 mb-2">🎯 Strategic Recommendations</h4>
                                    <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-1">
                                        <li>• Start with pilot-scale deployments</li>
                                        <li>• Focus on high-value markets first</li>
                                        <li>• Develop operational expertise early</li>
                                        <li>• Build strong utility partnerships</li>
                                        <li>• Monitor technology developments</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-600">
                                <h4 className="font-bold text-blue-800 dark:text-blue-200 mb-2">💡 Implementation Roadmap</h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                    <div className="text-center">
                                        <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                                            <span className="text-2xl">1️⃣</span>
                                            <h5 className="font-semibold text-blue-800 dark:text-blue-200 mt-2">Market Analysis</h5>
                                            <p className="text-blue-600 dark:text-blue-400 mt-1">
                                                Study local electricity markets, pricing patterns, and regulatory framework
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                                            <span className="text-2xl">2️⃣</span>
                                            <h5 className="font-semibold text-green-800 dark:text-green-200 mt-2">Technology Selection</h5>
                                            <p className="text-green-600 dark:text-green-400 mt-1">
                                                Choose proven battery technology with strong warranties and track record
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
                                            <span className="text-2xl">3️⃣</span>
                                            <h5 className="font-semibold text-purple-800 dark:text-purple-200 mt-2">Financial Modeling</h5>
                                            <p className="text-purple-600 dark:text-purple-400 mt-1">
                                                Develop comprehensive models including all revenue streams and costs
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg">
                                            <span className="text-2xl">4️⃣</span>
                                            <h5 className="font-semibold text-orange-800 dark:text-orange-200 mt-2">Implementation</h5>
                                            <p className="text-orange-600 dark:text-orange-400 mt-1">
                                                Execute with experienced partners and robust operational procedures
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'implementation':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🛠️ Technical Implementation</h2>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
                            <h3 className="text-xl font-bold text-purple-800 dark:text-purple-200 mb-4">🏗️ System Architecture</h3>
                            <p className="text-purple-700 dark:text-purple-300 mb-4">
                                The Solar Financial Model is built on a modern React.js foundation with a modular, scalable architecture that separates concerns 
                                between data calculation, state management, and user interface components.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">⚛️ Frontend</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">React 18+ with functional components and hooks</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">🧮 Engine</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Pure JavaScript calculation engine with real-time updates</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">📊 Visualization</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Recharts library for interactive charts and graphs</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Core Components</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">Input Management</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                            Real-time input validation, controlled components with useState hooks, and automatic recalculation triggers
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">Calculation Engine</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            Modular financial functions with dependency tracking, memoized calculations using useMemo for performance
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">UI Components</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                                            Responsive grid layouts, tabbed interface, modal dialogs, and dark/light theme support
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">Data Visualization</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                            Interactive charts with Recharts, responsive design, tooltips, and legend management
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Performance Features</h3>
                                <div className="space-y-4">
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-yellow-800 dark:text-yellow-200">Memoization Strategy</h4>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                                            <strong>useMemo:</strong> Complex calculations cached based on input dependencies<br/>
                                            <strong>useCallback:</strong> Event handlers optimized to prevent unnecessary re-renders<br/>
                                            <strong>React.memo:</strong> Component-level memoization for expensive UI components
                                        </p>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">Real-time Updates</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-2">
                                            <strong>Instant Recalculation:</strong> All financial metrics update immediately on input change<br/>
                                            <strong>Dependency Tracking:</strong> Only affected calculations are re-computed<br/>
                                            <strong>State Synchronization:</strong> Consistent data across all tabs and components
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Data Flow Architecture</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📝</span>
                                    <h4 className="font-bold text-blue-800 dark:text-blue-200">Input Layer</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">User inputs with validation</p>
                                </div>
                                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">⚙️</span>
                                    <h4 className="font-bold text-green-800 dark:text-green-200">Calculation Engine</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">Financial computations</p>
                                </div>
                                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📋</span>
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">Data Processing</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">Results formatting</p>
                                </div>
                                <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                    <span className="text-3xl block mb-2">📊</span>
                                    <h4 className="font-bold text-orange-800 dark:text-orange-200">Presentation Layer</h4>
                                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">Tables, charts, reports</p>
                                </div>
                            </div>
                            
                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">🔄 Calculation Dependencies</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                                    The system uses a dependency graph to ensure calculations are performed in the correct order:
                                </p>
                                <div className="text-xs text-gray-600 dark:text-gray-400 font-mono bg-white dark:bg-gray-800 p-3 rounded border">
                                    inputs → investmentAndFinancing → energyGeneration → costStructure → loanRepayment → 
                                    profitAndLoss → balanceSheet → cashFlows → indicators → evaStatement
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'usage':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📚 Usage Guidelines</h2>
                        
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-green-200 dark:border-green-700">
                            <h3 className="text-xl font-bold text-green-800 dark:text-green-200 mb-4">🎯 Target Users & Use Cases</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">💼 Financial Analysts</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Investment evaluation, due diligence, risk assessment</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-800 dark:text-green-200">🏗️ Project Developers</h4>
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">Feasibility studies, financing optimization, project planning</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">💰 Investors</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">Return analysis, portfolio evaluation, decision support</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🚀 Getting Started Workflow</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">1.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Project Configuration</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Start with Basic Data tab. Enter project size (MW), construction timeline, and operational period.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-green-600 dark:text-green-400 text-lg">2.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Investment Parameters</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Set capital costs ($/MW), equipment specifications, and initial funding structure.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-purple-600 dark:text-purple-400 text-lg">3.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Revenue & Cost Inputs</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Configure electricity tariff, capacity factor, O&M costs, and degradation rates.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-orange-600 dark:text-orange-400 text-lg">4.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Financing Structure</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Define loan terms, interest rates, and equity requirements.</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start space-x-3">
                                        <span className="font-bold text-red-600 dark:text-red-400 text-lg">5.</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800 dark:text-gray-100">Results Analysis</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">Review Summary tab, analyze financial statements, and assess viability.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Best Practices</h3>
                                <div className="space-y-4">
                                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">✅ Conservative Assumptions</h4>
                                        <ul className="text-sm text-green-700 dark:text-green-300 mt-2 space-y-1">
                                            <li>• Use P75-P90 capacity factors for risk management</li>
                                            <li>• Apply higher degradation rates (0.5-0.7% annually)</li>
                                            <li>• Include contingency in capital costs (5-10%)</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">📊 Sensitivity Testing</h4>
                                        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                                            <li>• Test key variables ±20% from base case</li>
                                            <li>• Focus on controllable vs. market factors</li>
                                            <li>• Document scenarios and stress tests</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">🔍 Results Validation</h4>
                                        <ul className="text-sm text-orange-700 dark:text-orange-300 mt-2 space-y-1">
                                            <li>• Cross-check balance sheet balancing</li>
                                            <li>• Verify cash flow consistency</li>
                                            <li>• Compare against industry benchmarks</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Advanced Analysis Techniques</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">🎯 Goal Seek Analysis</h4>
                                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Target IRR Optimization</h5>
                                            <p>Use Goal Seek to find required equity amount for desired return rate</p>
                                        </div>
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Break-even Analysis</h5>
                                            <p>Determine minimum tariff or maximum cost for project viability</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">📊 Scenario Planning</h4>
                                    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Three-Point Estimates</h5>
                                            <p>Model optimistic, base case, and pessimistic scenarios</p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">Stress Testing</h5>
                                            <p>Test extreme conditions: recession, rate hikes, technology disruption</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔧 Troubleshooting & Common Issues</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">❌ Common Errors</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Negative NPV:</strong> Check tariff rates and cost assumptions</li>
                                        <li>• <strong>Low IRR:</strong> Review financing structure and leverage</li>
                                        <li>• <strong>Unrealistic Payback:</strong> Validate operating cost percentages</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">✅ Quick Fixes</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Balance Sheet Issues:</strong> Check depreciation and loan balances</li>
                                        <li>• <strong>Cash Flow Problems:</strong> Verify interest calculations</li>
                                        <li>• <strong>Tax Anomalies:</strong> Review tax rate settings</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'extensions':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔮 Model Extensions & Future Enhancements</h2>
                        
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-700">
                            <h3 className="text-xl font-bold text-indigo-800 dark:text-indigo-200 mb-4">🚀 Roadmap Overview</h3>
                            <p className="text-indigo-700 dark:text-indigo-300 mb-4">
                                The Solar Financial Model is designed with extensibility in mind. Future enhancements will focus on advanced analytics, 
                                integration capabilities, and emerging technologies in the renewable energy sector.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-indigo-800 dark:text-indigo-200">📊 Analytics</h4>
                                    <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-2">Advanced modeling and AI-powered insights</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-800 dark:text-purple-200">🔗 Integration</h4>
                                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">External data sources and API connectivity</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">⚡ Technology</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Emerging solar technologies and market trends</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📈 Advanced Analytics Features</h3>
                                <div className="space-y-4">
                                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">🎲 Monte Carlo Simulation</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                            Probabilistic modeling with 1000+ scenarios, risk distributions, and confidence intervals for NPV/IRR
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                                            <strong>Status:</strong> Planned for v2.0 | <strong>Impact:</strong> Enhanced risk assessment
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">🤖 Machine Learning Optimization</h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            AI-powered parameter optimization for maximum IRR, automatic scenario generation, and predictive analytics
                                        </p>
                                        <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                            <strong>Status:</strong> Research phase | <strong>Impact:</strong> Automated optimization
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">📊 Portfolio Analysis</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                                            Multi-project portfolio optimization, correlation analysis, and diversification benefits assessment
                                        </p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                                            <strong>Status:</strong> Conceptual | <strong>Impact:</strong> Portfolio-level insights
                                        </p>
                                    </div>
                                    
                                    <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-r-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">🌡️ Climate Risk Modeling</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                                            Weather pattern analysis, climate change impact on generation, and extreme weather risk assessment
                                        </p>
                                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                                            <strong>Status:</strong> Planned for v2.5 | <strong>Impact:</strong> Climate-aware projections
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🔗 Integration & Connectivity</h3>
                                <div className="space-y-4">
                                    <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-cyan-800 dark:text-cyan-200">🌐 Market Data APIs</h4>
                                        <p className="text-sm text-cyan-700 dark:text-cyan-300 mt-2">
                                            <strong>Real-time Integration:</strong> Electricity prices, equipment costs, financing rates<br/>
                                            <strong>Data Sources:</strong> IRENA, NREL, Bloomberg New Energy Finance<br/>
                                            <strong>Update Frequency:</strong> Daily/weekly automated refreshes
                                        </p>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">☁️ Weather Data Integration</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            <strong>Solar Resource:</strong> Historical and forecast irradiation data<br/>
                                            <strong>Providers:</strong> NREL NSRDB, SolarGIS, Meteonorm<br/>
                                            <strong>Features:</strong> Site-specific analysis, P50/P90 estimates
                                        </p>
                                    </div>
                                    
                                    <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-pink-800 dark:text-pink-200">📋 ERP System Connectivity</h4>
                                        <p className="text-sm text-pink-700 dark:text-pink-300 mt-2">
                                            <strong>Export Capabilities:</strong> Excel, CSV, JSON, PDF reports<br/>
                                            <strong>Import Functions:</strong> Project templates, bulk data updates<br/>
                                            <strong>API Endpoints:</strong> RESTful API for system integration
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Technology & Market Extensions</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-yellow-800 dark:text-yellow-200">🔋 Energy Storage Integration</h4>
                                    <div className="space-y-3">
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Battery Storage Modeling</h5>
                                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                                LCOE optimization, capacity sizing, degradation modeling
                                            </p>
                                        </div>
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-orange-700 dark:text-orange-300">Grid Services Revenue</h5>
                                            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                                Ancillary services, frequency regulation, peak shaving
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <h4 className="font-bold text-green-800 dark:text-green-200">🌍 Carbon Market Integration</h4>
                                    <div className="space-y-3">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Carbon Credit Revenue</h5>
                                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                                Voluntary and compliance markets, pricing models
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">ESG Impact Metrics</h5>
                                            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                                CO₂ reduction, sustainability scoring, impact reporting
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <h4 className="font-bold text-purple-800 dark:text-purple-200">⚙️ Technology Evolution</h4>
                                    <div className="space-y-3">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Bifacial & Tracking</h5>
                                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                Advanced module technologies, tracking system benefits
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Agrivoltaics Modeling</h5>
                                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                                                Dual land use, agricultural revenue integration
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-blue-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🛣️ Development Timeline</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🎯</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q2 2025</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Monte Carlo simulation</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Market data APIs</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🔋</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q4 2025</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Energy storage modeling</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Portfolio analysis</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🤖</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">Q2 2026</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">ML optimization</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Climate risk modeling</p>
                                </div>
                                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-2xl block mb-2">🌍</span>
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100">2027+</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Carbon markets</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Agrivoltaics support</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'theory':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">🔬 Financial Theory & Methodologies</h2>
                        
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-6 rounded-lg border border-teal-200 dark:border-teal-700">
                            <h3 className="text-xl font-bold text-teal-800 dark:text-teal-200 mb-4">🧮 Theoretical Foundation</h3>
                            <p className="text-teal-700 dark:text-teal-300 mb-4">
                                The Solar Financial Model is built upon established financial theories and industry best practices, 
                                ensuring academic rigor and professional standards in renewable energy project evaluation.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-teal-800 dark:text-teal-200">💰 DCF Theory</h4>
                                    <p className="text-sm text-teal-600 dark:text-teal-400 mt-2">Discounted cash flow fundamentals and time value of money</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-cyan-800 dark:text-cyan-200">📊 Capital Structure</h4>
                                    <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-2">Optimal debt-equity mix and cost of capital theory</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">⚖️ Risk Assessment</h4>
                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">Modern portfolio theory and risk-return optimization</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">💰 Discounted Cash Flow (DCF) Theory</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-blue-800 dark:text-blue-200">🎯 Core Principle</h4>
                                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                                            The intrinsic value of a project equals the present value of its expected future cash flows, 
                                            discounted at an appropriate risk-adjusted rate that reflects the project's systematic risk.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-green-800 dark:text-green-200">📐 Mathematical Foundation</h4>
                                        <div className="text-sm text-green-700 dark:text-green-300 mt-2 space-y-2">
                                            <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded text-xs">
                                                NPV = Σ(CFₜ / (1 + r)ᵗ) - I₀
                                            </div>
                                            <p><strong>Where:</strong> CFₜ = Cash flow in period t, r = Discount rate, I₀ = Initial investment</p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-purple-800 dark:text-purple-200">🔄 IRR Methodology</h4>
                                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-2">
                                            Internal Rate of Return represents the discount rate that makes NPV = 0, calculated using 
                                            Newton-Raphson iteration method for numerical precision in multi-period cash flows.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-orange-800 dark:text-orange-200">⏱️ Time Value Considerations</h4>
                                        <p className="text-sm text-orange-700 dark:text-orange-300 mt-2">
                                            Accounts for inflation, opportunity cost, and investment risk through the discount rate, 
                                            with different rates for project vs. equity cash flows reflecting leverage effects.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Capital Structure Theory</h3>
                                <div className="space-y-4">
                                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-indigo-800 dark:text-indigo-200">⚖️ Modigliani-Miller Framework</h4>
                                        <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-2">
                                            In perfect markets, capital structure doesn't affect firm value. However, in practice, 
                                            tax benefits of debt and financial distress costs create an optimal leverage point.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-cyan-800 dark:text-cyan-200">🛡️ Tax Shield Benefits</h4>
                                        <div className="text-sm text-cyan-700 dark:text-cyan-300 mt-2 space-y-2">
                                            <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded text-xs">
                                                Tax Shield = Interest Expense × Tax Rate
                                            </div>
                                            <p>Debt financing creates tax deductions that increase project value</p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-teal-800 dark:text-teal-200">💡 WACC Optimization</h4>
                                        <p className="text-sm text-teal-700 dark:text-teal-300 mt-2">
                                            Weighted Average Cost of Capital minimizes at optimal debt-equity ratio, 
                                            balancing tax benefits against increased financial risk and cost of financial distress.
                                        </p>
                                    </div>
                                    
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
                                        <h4 className="font-bold text-emerald-800 dark:text-emerald-200">📈 Leverage Effects</h4>
                                        <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                                            Financial leverage amplifies equity returns but also increases risk. 
                                            Solar projects typically support 70-80% debt ratios due to predictable cash flows.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚖️ Risk Assessment & Valuation Theory</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-3">📊 Modern Portfolio Theory (MPT)</h4>
                                    <div className="space-y-3">
                                        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-red-700 dark:text-red-300">Risk-Return Optimization</h5>
                                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                                Systematic risk (beta) determines required return; unsystematic risk can be diversified away
                                            </p>
                                        </div>
                                        <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-pink-700 dark:text-pink-300">Capital Asset Pricing Model</h5>
                                            <p className="text-xs text-pink-600 dark:text-pink-400 mt-1">
                                                Required return = Risk-free rate + Beta × Market risk premium
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">🎯 Real Options Theory</h4>
                                    <div className="space-y-3">
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-blue-700 dark:text-blue-300">Expansion Options</h5>
                                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                Value of ability to expand capacity if market conditions improve
                                            </p>
                                        </div>
                                        <div className="bg-sky-50 dark:bg-sky-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-sky-700 dark:text-sky-300">Abandonment Options</h5>
                                            <p className="text-xs text-sky-600 dark:text-sky-400 mt-1">
                                                Value of salvage option if project becomes uneconomical
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">⚡ Renewable Energy Finance Specifics</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">🌞 Asset Characteristics</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-yellow-700 dark:text-yellow-300">Predictable Cash Flows</h5>
                                            <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                                                Solar irradiation patterns are well-understood, enabling accurate revenue forecasting
                                            </p>
                                        </div>
                                        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-amber-700 dark:text-amber-300">Long Asset Life</h5>
                                            <p className="text-amber-600 dark:text-amber-400 mt-1">
                                                25+ year operational periods with predictable degradation patterns
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">💰 Revenue Models</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-green-700 dark:text-green-300">Power Purchase Agreements</h5>
                                            <p className="text-green-600 dark:text-green-400 mt-1">
                                                Long-term contracts provide revenue certainty and support project financing
                                            </p>
                                        </div>
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-emerald-700 dark:text-emerald-300">Merchant Risk</h5>
                                            <p className="text-emerald-600 dark:text-emerald-400 mt-1">
                                                Exposure to spot electricity prices requires sophisticated risk management
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-3">🔄 Economic Value Added</h4>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-purple-700 dark:text-purple-300">Value Creation Measurement</h5>
                                            <p className="text-purple-600 dark:text-purple-400 mt-1">
                                                EVA = NOPAT - (Capital × WACC) measures true economic profit
                                            </p>
                                        </div>
                                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg">
                                            <h5 className="font-semibold text-indigo-700 dark:text-indigo-300">Stakeholder Value</h5>
                                            <p className="text-indigo-600 dark:text-indigo-400 mt-1">
                                                Positive EVA indicates value creation above cost of capital
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">📚 Academic References & Standards</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">📖 Core Financial Theory</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>Brealey, Myers & Allen:</strong> Principles of Corporate Finance</li>
                                        <li>• <strong>Damodaran:</strong> Investment Valuation & Corporate Finance</li>
                                        <li>• <strong>Ross, Westerfield & Jaffe:</strong> Modern Financial Management</li>
                                        <li>• <strong>Copeland, Koller & Murrin:</strong> Valuation Methods</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">⚡ Renewable Energy Finance</h4>
                                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <li>• <strong>IRENA:</strong> Renewable Energy Finance Guidelines</li>
                                        <li>• <strong>IEA:</strong> Energy Technology Investment Standards</li>
                                        <li>• <strong>NREL:</strong> Solar Project Financial Modeling Best Practices</li>
                                        <li>• <strong>Bloomberg NEF:</strong> Clean Energy Investment Methodologies</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'about':
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">👨‍💼 About This Model</h2>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 p-8 rounded-xl border border-blue-200 dark:border-blue-700">
                            <div className="text-center">
                                <div className="mb-6">
                                    <span className="text-6xl">⚡🔋</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                                    Solar + BESS Financial Model
                                </h3>
                                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                                    A comprehensive financial analysis tool for utility-scale solar energy projects with battery energy storage integration
                                </p>
                                
                                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-lg">
                                    <div className="flex items-center justify-center space-x-3 mb-4">
                                        <span className="text-2xl">👨‍💼</span>
                                        <div className="text-left">
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100">Created by</h4>
                                            <p className="text-lg text-blue-600 dark:text-blue-400 font-semibold">Fernando Izeta</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center space-x-3">
                                        <span className="text-2xl">📅</span>
                                        <div className="text-left">
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100">Date</h4>
                                            <p className="text-lg text-green-600 dark:text-green-400 font-semibold">July 12th, 2025</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">🎯</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Purpose</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Provide comprehensive financial analysis and decision-making tools for solar energy investments
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">⚡</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Technology</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Built with React, featuring interactive calculations, real-time updates, and comprehensive reporting
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="text-center">
                                    <span className="text-3xl mb-3 block">📊</span>
                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">Features</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        9 analysis tabs, sensitivity analysis, goal seek functionality, and exportable documentation
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">🌟 Model Highlights</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">Financial Analysis</h4>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                        <li>• Complete DCF analysis with NPV and IRR calculations</li>
                                        <li>• Detailed P&L, balance sheet, and cash flow projections</li>
                                        <li>• Economic Value Added (EVA) analysis</li>
                                        <li>• Comprehensive cost modeling including degradation</li>
                                        <li>• Solar + BESS integrated financial modeling</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-3">🔋 BESS Integration</h4>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                        <li>• Multi-revenue stream modeling (arbitrage, ancillary, capacity)</li>
                                        <li>• Battery degradation and replacement scheduling</li>
                                        <li>• Energy storage optimization strategies</li>
                                        <li>• Grid services monetization analysis</li>
                                        <li>• Comprehensive BESS financial impact assessment</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">Decision Support</h4>
                                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                        <li>• Sensitivity analysis for key variables</li>
                                        <li>• Goal seek optimization for target returns</li>
                                        <li>• Risk assessment and scenario planning</li>
                                        <li>• Investment decision framework</li>
                                        <li>• BESS sizing and configuration optimization</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-center">
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    This model represents a comprehensive approach to solar + BESS project financial analysis, 
                                    combining industry best practices with advanced financial modeling techniques and energy storage optimization.
                                </p>
                                <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Version 1.1 | Solar + BESS Financial Model | © 2025 Fernando Izeta
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="space-y-8">
                        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">📚 Additional Documentation</h2>
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                            <p className="text-gray-600 dark:text-gray-300">
                                Select a section from the navigation menu to view detailed documentation.
                            </p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {/* Navigation Sidebar */}
            <div className="lg:w-1/4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sticky top-4">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">📖 Documentation Sections</h3>
                    <nav className="space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors duration-200 ${
                                    activeSection === item.id
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                <span className="text-sm">{item.icon}</span> {item.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    {renderSection()}
                </div>
            </div>
        </div>
    );
};

const ChartsTab = ({ outputs, inputs, isDarkMode }) => {
    const pnlChartData = useMemo(() => outputs.pnlStatement?.length > 0 ? outputs.pnlStatement.map(p => ({name: `Year ${p.year}`, 'Operating Income': p.operatingIncome, 'Net Profit': p.netProfit, 'Total Profit': p.totalProfit})) : [], [outputs.pnlStatement]);
    const costChartData = useMemo(() => outputs.costStatement?.length > 0 ? outputs.costStatement.map(c => ({name: `Year ${c.year}`, 'Operating Cost': c.operatingCost, 'Land Cost': c.landCost, 'Depreciation': c.depreciation, 'Interest': c.interest})) : [], [outputs.costStatement]);
    const tickColor = isDarkMode ? '#A0AEC0' : '#4A5568';
    return (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Financial Charts</h2><div className="grid lg:grid-cols-2 gap-8">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg h-96"><h3 className="font-semibold mb-4">Profitability Over Time</h3><ResponsiveContainer width="100%" height="100%"><LineChart data={pnlChartData}><CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4A5568' : '#E2E8F0'} /><XAxis dataKey="name" tick={{ fill: tickColor }} /><YAxis tickFormatter={(v) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(v)} tick={{ fill: tickColor }}/><Tooltip formatter={(value) => formatCurrency(value, 0)} contentStyle={{ backgroundColor: isDarkMode ? '#2D3748' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#4A5568' : '#E2E8F0'}` }} /><Legend wrapperStyle={{color: tickColor}} /><Line type="monotone" dataKey="Operating Income" stroke="#3b82f6" /><Line type="monotone" dataKey="Net Profit" stroke="#16a34a" /></LineChart></ResponsiveContainer></div>
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg h-96"><h3 className="font-semibold mb-4">Cost Structure Over Time</h3><ResponsiveContainer width="100%" height="100%"><BarChart data={costChartData}><CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#4A5568' : '#E2E8F0'} /><XAxis dataKey="name" tick={{ fill: tickColor }}/><YAxis tickFormatter={(v) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(v)} tick={{ fill: tickColor }}/><Tooltip formatter={(value) => formatCurrency(value, 0)} contentStyle={{ backgroundColor: isDarkMode ? '#2D3748' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#4A5568' : '#E2E8F0'}` }} /><Legend wrapperStyle={{color: tickColor}} /><Bar dataKey="Operating Cost" stackId="a" fill="#8884d8" /><Bar dataKey="Land Cost" stackId="a" fill="#ff7c7c" /><Bar dataKey="Depreciation" stackId="a" fill="#82ca9d" /><Bar dataKey="Interest" stackId="a" fill="#ffc658" /></BarChart></ResponsiveContainer></div>
    </div>
    <Explanation title="How This Works">
        <p>This tab provides a visual representation of the key data from the financial statements, making it easier to spot trends and understand relationships.</p>
        <p><strong>Profitability Over Time:</strong> This line chart shows the project's revenue (Operating Income) versus its Net Profit over the entire operational period. It helps to quickly assess the overall profitability trend and when the project becomes most profitable.</p>
        <p><strong>Cost Structure Over Time:</strong> This stacked bar chart breaks down the total annual costs into their main components (Operating Costs, Depreciation, and Interest). It is useful for understanding how the cost composition changes over the project's lifetime, such as when interest costs decrease as the loan is paid down.</p>
        <p><strong>Net Profit Pattern Explained:</strong> {inputs.bessEnabled ? (
            (() => {
                const replacementPeriod = inputs.bessReplacementPeriod;
                const degradationRate = inputs.bessDegradationRate;
                const degradationAtReplacement = Math.pow((1 - degradationRate), replacementPeriod - 1);
                const performanceAtReplacement = (degradationAtReplacement * 100).toFixed(1);
                return `If you notice net profit increasing over time, especially around year ${replacementPeriod}, this is expected and realistic! This pattern occurs due to several factors: (1) BESS Replacement Recovery - When batteries are replaced every ${replacementPeriod} years, energy-based revenues (arbitrage, ancillary services) jump back to 100% efficiency from ~${performanceAtReplacement}% degraded performance, creating a significant revenue boost; (2) Debt Service Reduction - As loans are paid down, interest expenses decrease while revenues remain stable; (3) Operating Leverage - Fixed costs are spread over maintained or recovered revenue streams. This "sawtooth" profit pattern with periodic jumps matches real-world battery storage project economics where operators plan for performance recovery through scheduled replacements.`;
            })()
        ) : "If you notice net profit increasing over time, this typically occurs due to debt service reduction as loans are paid down while revenues remain stable, creating operating leverage where fixed costs are spread over maintained revenue streams."}</p>
    </Explanation>
    </div>);
};
